// Calls phase — unified inbox for all 3 recording kinds (2026-04-29).
//
// Replaces the standalone "Qualifying Calls" phase. Houses:
//   - Upcoming bots awaiting confirmation (Demo vs Qualifying vs Meeting)
//   - Completed recordings of any kind, with a type-pill filter at top
//
// Click a row → drill into the type-appropriate detail surface:
//   - Demo:           routes to App.jsx 'completed' phase via onOpenDemo
//   - Qualifying call: existing CompletedDetail (qualification summary)
//   - Meeting:        new MeetingDetail (generic summary shape)
//
// Every detail surface mounts the shared ReclassifyModal so a
// mislabelled call can be re-tagged in two clicks.
//
// Hash routing: #/calls (list), #/calls/<id> (detail). App.jsx maps
// phase 'calls' to this component.

import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import {
  Stat,
  Divider,
  TalkingTimeline,
  TranscriptPanel,
  getSpeakerColor,
  fmtDuration,
  computeSpeakerStats,
  applySpeakerNameLayers,
} from './CallReviewSections';
import ReclassifyModal from './ReclassifyModal';
import CommentsThread from './CommentsThread';
import TopicChapterStrip from './TopicChapterStrip';
import AskWinterCallTab from './AskWinterCallTab';

const API_BASE = process.env.REACT_APP_API_URL || 'https://trailercst-backend-production.up.railway.app';

function fmtMeetingTime(s) {
  if (!s) return 'Unknown time';
  // NB: new Date(bad) doesn't throw — it yields an Invalid Date whose
  // toLocaleString() is the literal string "Invalid Date". Check the
  // timestamp explicitly instead of relying on try/catch.
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return 'Unknown time';
  return d.toLocaleString(undefined, {
    month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

function fmtDate(s) {
  if (!s) return '';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

// Parse the URL hash for #/calls/<id>. The phase token is owned by
// App.jsx; we only read what comes after.
function parseDetailHash() {
  if (typeof window === 'undefined') return null;
  const raw = (window.location.hash || '').replace(/^#\/?/, '');
  const parts = raw.split('/');
  if (parts[0] !== 'calls') return null;
  return parts[1] || null;
}

export default function CallsPhase({ authToken, setPhase, onOpenDemo, authUser }) {
  // selectedCallId hydrates from the hash so a refresh on the detail
  // view stays on the call instead of bouncing back to the list.
  const [selectedCallId, setSelectedCallId] = useState(() => parseDetailHash());
  const [tab, setTab] = useState('upcoming'); // 'upcoming' | 'completed'
  // calls_inbox_layout (2026-09-04): recordings are the page, upcoming
  // notetakers are a strip above them, no Upcoming/Completed tabs and
  // no calendar views. Flag-off keeps the tabbed screen byte-for-byte.
  const inboxLayout = !!authUser?.company?.featureFlags?.calls_inbox_layout;

  // Mirror selectedCallId ↔ hash. Same pattern as BuyerSpacesPhase,
  // except entering a detail PUSHES a history entry so the browser
  // Back button returns to the list (replaceState left nothing to pop,
  // so Back exited the page entirely).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const target = selectedCallId
      ? `#/calls/${selectedCallId}`
      : '#/calls';
    if (window.location.hash !== target) {
      const url = window.location.pathname + window.location.search + target;
      if (selectedCallId) {
        window.history.pushState(null, '', url);
      } else {
        window.history.replaceState(null, '', url);
      }
    }
  }, [selectedCallId]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onPop = () => setSelectedCallId(parseDetailHash());
    window.addEventListener('popstate', onPop);
    window.addEventListener('hashchange', onPop);
    return () => {
      window.removeEventListener('popstate', onPop);
      window.removeEventListener('hashchange', onPop);
    };
  }, []);

  if (selectedCallId) {
    return (
      <CallDetailDispatcher
        authToken={authToken}
        callId={selectedCallId}
        onBack={() => setSelectedCallId(null)}
        setPhase={setPhase}
        onOpenDemo={onOpenDemo}
        authUser={authUser}
      />
    );
  }

  return (
    <div style={{
      // 2026-05-08: dropped the per-phase gradient backdrop. Same
      // reasoning as BuyerSpacesPhase — confined gradient created
      // a color seam against the page background under the new
      // edge-to-edge layout.
      minHeight: '100vh',
      color: 'var(--room-text-strong)',
    }}>
      <div style={{ maxWidth: 1600, margin: '0 auto', padding: '40px 24px 80px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 24, marginBottom: 12, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 280 }}>
            <h1 style={{
              margin: 0, fontSize: 36, fontWeight: 700, letterSpacing: '-0.02em',
              background: 'linear-gradient(135deg, #0891b2 0%, #0ea5e9 50%, #6366f1 100%)',
              WebkitBackgroundClip: 'text', backgroundClip: 'text',
              WebkitTextFillColor: 'transparent', color: 'transparent',
            }}>
              Calls
            </h1>
            <p style={{ margin: '8px 0 0', color: 'var(--room-text-muted)', fontSize: 15, maxWidth: 620 }}>
              {inboxLayout
                ? 'Every recording, newest first — demos, qualifying calls, and meetings. Notetakers scheduled for upcoming meetings sit above the list.'
                : 'Every recorded call lands here — demos, qualifying calls, and meetings. Filter by type, drill in to review, or re-tag a mislabelled call to kick the right pipeline.'}
            </p>
          </div>
          <button
            type="button"
            className="back-to-dashboard"
            onClick={() => setPhase('dashboard')}
          >
            ← Back to dashboard
          </button>
        </div>

        {inboxLayout && (
          <CallsInbox
            authToken={authToken}
            onSelect={setSelectedCallId}
            onOpenDemo={onOpenDemo}
            authUser={authUser}
            setPhase={setPhase}
          />
        )}

        {!inboxLayout && (
        <div style={{
          display: 'flex', gap: 4, borderBottom: '1px solid var(--room-border, #e2e8f0)',
          margin: '24px 0 24px',
        }}>
          <TabButton active={tab === 'upcoming'} onClick={() => setTab('upcoming')}>
            Upcoming
          </TabButton>
          <TabButton active={tab === 'completed'} onClick={() => setTab('completed')}>
            Completed
          </TabButton>
        </div>
        )}

        {!inboxLayout && tab === 'upcoming' && (
          <UpcomingTab
            authToken={authToken}
            onSelect={setSelectedCallId}
            joinEnabled={!!authUser?.company?.featureFlags?.upcoming_calls_join}
          />
        )}
        {!inboxLayout && tab === 'completed' && (
          <CompletedTab
            authToken={authToken}
            onSelect={setSelectedCallId}
            onOpenDemo={onOpenDemo}
            isAdmin={!!(authUser && authUser.company && authUser.company.isAdmin)}
            isGlobalAdmin={!!(authUser && authUser.company && authUser.company.isGlobalAdmin)}
          />
        )}
      </div>
    </div>
  );
}

function TabButton({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: '12px 20px', border: 'none', background: 'none',
        cursor: 'pointer', fontFamily: 'inherit',
        fontSize: 14, fontWeight: 600,
        color: active ? '#0891b2' : 'var(--room-text-muted, #64748b)',
        borderBottom: active ? '2px solid #0891b2' : '2px solid transparent',
        marginBottom: -1,
      }}
    >
      {children}
    </button>
  );
}

// ─── Upcoming tab ────────────────────────────────────────────────

function UpcomingTab({ authToken, onSelect, joinEnabled = false }) {
  const [bots, setBots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Default to 'list' — most users open Upcoming to see the next few
  // calls + take quick action (confirm kind, exclude notetaker), and
  // the list view is where every per-row action lives. Day/Week/Month
  // calendar views are visual contexts, no actions.
  const [view, setView] = useState('list'); // 'list' | 'day' | 'week' | 'month'
  const [anchorDate, setAnchorDate] = useState(() => new Date());

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    fetch(`${API_BASE}/api/qualifying-calls/upcoming`, {
      headers: { Authorization: `Bearer ${authToken}` },
    })
      .then(async (r) => {
        if (!r.ok) {
          const body = await r.json().catch(() => ({}));
          throw new Error(body.error || `Failed to load (status ${r.status})`);
        }
        return r.json();
      })
      .then((data) => {
        setBots(Array.isArray(data.upcoming) ? data.upcoming : []);
        setLoading(false);
      })
      .catch((err) => { setError(err.message); setLoading(false); });
  }, [authToken]);

  useEffect(() => { load(); }, [load]);

  // Sort: unconfirmed bots first (action-required), then by start time.
  // Used by the list view AND by per-day/week filtering downstream.
  const sorted = useMemo(() => {
    return [...bots].sort((a, b) => {
      if (a.recordingKindConfirmed !== b.recordingKindConfirmed) {
        return a.recordingKindConfirmed ? 1 : -1;
      }
      return new Date(a.meetingStartAt || 0) - new Date(b.meetingStartAt || 0);
    });
  }, [bots]);

  return (
    <div>
      {/* View-mode pill row + date navigator */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 12, marginBottom: 16, flexWrap: 'wrap',
      }}>
        <div style={{ display: 'inline-flex', gap: 4, padding: 4, borderRadius: 12, background: 'var(--room-card-hover)', border: '1px solid var(--room-border)' }}>
          {[
            { id: 'list',  label: 'List' },
            { id: 'day',   label: 'Day' },
            { id: 'week',  label: 'Week' },
            { id: 'month', label: 'Month' },
          ].map((opt) => {
            const active = view === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => setView(opt.id)}
                style={{
                  padding: '7px 14px', borderRadius: 8, border: 'none',
                  background: active ? 'var(--room-card)' : 'transparent',
                  color: active ? 'var(--room-text-strong)' : 'var(--room-text-muted)',
                  fontWeight: 600, fontSize: 13, fontFamily: 'inherit',
                  cursor: 'pointer',
                  boxShadow: active ? '0 1px 3px rgba(15, 23, 42, 0.08)' : 'none',
                  transition: 'all 0.12s ease',
                }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>

        {view !== 'list' && (
          <CalendarNav
            view={view}
            anchorDate={anchorDate}
            onPrev={() => setAnchorDate((d) => shiftDate(d, view, -1))}
            onNext={() => setAnchorDate((d) => shiftDate(d, view, +1))}
            onToday={() => setAnchorDate(new Date())}
          />
        )}
      </div>

      {loading && <p style={{ color: 'var(--room-text-muted)' }}>Loading upcoming calls…</p>}
      {error && <ErrorBox error={error} />}
      {!loading && !error && bots.length === 0 && (
        <EmptyState
          title="No calls scheduled"
          body="When calendar polling schedules a meeting bot, it'll appear here so you can confirm whether it's a qualifying call or a demo before the bot joins."
        />
      )}

      {!loading && !error && bots.length > 0 && view === 'list' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {sorted.map((bot) => (
            <UpcomingRow
              key={bot.id}
              bot={bot}
              authToken={authToken}
              onChanged={load}
              onClick={() => onSelect(bot.recallBotId)}
              joinEnabled={joinEnabled}
            />
          ))}
        </div>
      )}

      {!loading && !error && bots.length > 0 && view === 'day' && (
        <DayCalendar bots={sorted} anchorDate={anchorDate} onSelect={onSelect} />
      )}

      {!loading && !error && bots.length > 0 && view === 'week' && (
        <WeekCalendar bots={sorted} anchorDate={anchorDate} onSelect={onSelect} />
      )}

      {!loading && !error && bots.length > 0 && view === 'month' && (
        <MonthCalendar bots={sorted} anchorDate={anchorDate} onSelect={onSelect} />
      )}
    </div>
  );
}

// ─── Calendar primitives ─────────────────────────────────────────

function shiftDate(date, view, direction) {
  const d = new Date(date);
  if (view === 'day') d.setDate(d.getDate() + direction);
  else if (view === 'week') d.setDate(d.getDate() + 7 * direction);
  else if (view === 'month') d.setMonth(d.getMonth() + direction);
  return d;
}

function startOfDay(d) {
  const r = new Date(d); r.setHours(0, 0, 0, 0); return r;
}
function startOfWeek(d) {
  // Week starts Sunday — most common in NA business apps. Easy to flip later.
  const r = startOfDay(d);
  r.setDate(r.getDate() - r.getDay());
  return r;
}
function startOfMonth(d) {
  const r = startOfDay(d); r.setDate(1); return r;
}
function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
function fmtTime(d) {
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}
function fmtMonthDay(d) {
  return d.toLocaleDateString(undefined, { month: 'long', day: 'numeric' });
}

function CalendarNav({ view, anchorDate, onPrev, onNext, onToday }) {
  const label = view === 'day'
    ? anchorDate.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
    : view === 'week'
    ? (() => {
        const start = startOfWeek(anchorDate);
        const end = new Date(start); end.setDate(start.getDate() + 6);
        const startLabel = start.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
        const endLabel = end.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
        return `${startLabel} – ${endLabel}`;
      })()
    : anchorDate.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });

  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <button type="button" onClick={onPrev} style={navBtn}>‹</button>
      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--room-text-body)', minWidth: 200, textAlign: 'center' }}>
        {label}
      </span>
      <button type="button" onClick={onNext} style={navBtn}>›</button>
      <button type="button" onClick={onToday} style={{ ...navBtn, padding: '6px 12px', marginLeft: 8 }}>
        Today
      </button>
    </div>
  );
}

const navBtn = {
  padding: '6px 10px', borderRadius: 8,
  border: '1px solid var(--room-border)', background: 'var(--room-card)',
  color: 'var(--room-text-body)', cursor: 'pointer',
  fontFamily: 'inherit', fontSize: 14, fontWeight: 600,
  lineHeight: 1,
};

// Compact card used inside calendar cells. Tap → opens detail.
function CalendarChip({ bot, onClick, compact = false }) {
  const start = bot.meetingStartAt ? new Date(bot.meetingStartAt) : null;
  const kind = bot.recordingKind || 'qualifying_call';
  const color = kind === 'demo' ? '#9333ea' : kind === 'meeting' ? '#6366f1' : '#0891b2';
  return (
    <button
      type="button"
      onClick={onClick}
      title={bot.meetingTitle || '(untitled meeting)'}
      style={{
        display: 'block', textAlign: 'left',
        width: '100%',
        padding: compact ? '4px 6px' : '6px 8px',
        borderRadius: 6,
        background: `${color}1a`,
        border: `1px solid ${color}33`,
        borderLeft: `3px solid ${color}`,
        color: 'var(--room-text-strong)', cursor: 'pointer',
        fontFamily: 'inherit',
        fontSize: compact ? 11 : 12,
        lineHeight: 1.3,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        marginBottom: 2,
      }}
    >
      {start && (
        <span style={{ fontWeight: 700, color, marginRight: 4, fontVariantNumeric: 'tabular-nums' }}>
          {fmtTime(start)}
        </span>
      )}
      {bot.meetingTitle || '(untitled)'}
    </button>
  );
}

function DayCalendar({ bots, anchorDate, onSelect }) {
  const dayBots = bots.filter((b) => b.meetingStartAt && isSameDay(new Date(b.meetingStartAt), anchorDate));
  // Bucket into hour rows (8am-8pm shown by default; out-of-range shown above/below).
  const HOURS = Array.from({ length: 13 }, (_, i) => i + 8); // 8 → 20
  const beforeHours = dayBots.filter((b) => new Date(b.meetingStartAt).getHours() < 8);
  const afterHours = dayBots.filter((b) => new Date(b.meetingStartAt).getHours() > 20);

  if (dayBots.length === 0) {
    return (
      <div style={emptyDayStyle}>Nothing scheduled for {fmtMonthDay(anchorDate)}.</div>
    );
  }

  const botsAtHour = (h) => dayBots.filter((b) => new Date(b.meetingStartAt).getHours() === h);

  return (
    <div style={{ background: 'var(--room-card)', borderRadius: 12, border: '1px solid var(--room-border)', overflow: 'hidden' }}>
      {beforeHours.length > 0 && (
        <div style={hourBlockStyle}>
          <div style={hourLabelStyle}>before 8am</div>
          <div style={{ flex: 1 }}>
            {beforeHours.map((b) => (
              <CalendarChip key={b.id} bot={b} onClick={() => onSelect(b.recallBotId)} />
            ))}
          </div>
        </div>
      )}
      {HOURS.map((h) => {
        const bs = botsAtHour(h);
        return (
          <div key={h} style={hourBlockStyle}>
            <div style={hourLabelStyle}>
              {h === 12 ? '12pm' : h < 12 ? `${h}am` : `${h - 12}pm`}
            </div>
            <div style={{ flex: 1, minHeight: 28 }}>
              {bs.map((b) => (
                <CalendarChip key={b.id} bot={b} onClick={() => onSelect(b.recallBotId)} />
              ))}
            </div>
          </div>
        );
      })}
      {afterHours.length > 0 && (
        <div style={hourBlockStyle}>
          <div style={hourLabelStyle}>after 8pm</div>
          <div style={{ flex: 1 }}>
            {afterHours.map((b) => (
              <CalendarChip key={b.id} bot={b} onClick={() => onSelect(b.recallBotId)} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const hourBlockStyle = {
  display: 'flex', alignItems: 'flex-start',
  borderBottom: '1px solid var(--room-border-soft)',
  padding: '6px 12px',
};
const hourLabelStyle = {
  width: 64, color: 'var(--room-text-subtle)', fontSize: 11, fontWeight: 600,
  fontVariantNumeric: 'tabular-nums', paddingTop: 4,
  textTransform: 'lowercase', letterSpacing: '0.04em',
};
const emptyDayStyle = {
  background: 'var(--room-card)', borderRadius: 12, border: '1px dashed var(--room-border, #cbd5e1)',
  padding: '32px 24px', textAlign: 'center', color: 'var(--room-text-subtle)', fontSize: 14,
};

function WeekCalendar({ bots, anchorDate, onSelect }) {
  const start = startOfWeek(anchorDate);
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start); d.setDate(start.getDate() + i); return d;
  });
  const today = startOfDay(new Date());

  return (
    <div style={{
      display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)',
      gap: 8,
    }}>
      {days.map((day) => {
        const dayBots = bots.filter((b) => b.meetingStartAt && isSameDay(new Date(b.meetingStartAt), day));
        const isToday = isSameDay(day, today);
        return (
          <div
            key={day.toISOString()}
            style={{
              background: 'var(--room-card)',
              borderRadius: 10,
              border: `1px solid ${isToday ? '#0891b2' : 'var(--room-border, #e2e8f0)'}`,
              minHeight: 220,
              padding: 10,
              display: 'flex', flexDirection: 'column',
              boxShadow: isToday ? '0 0 0 1px #0891b2' : 'none',
            }}
          >
            <div style={{
              fontSize: 11, fontWeight: 700, color: 'var(--room-text-subtle)',
              textTransform: 'uppercase', letterSpacing: '0.05em',
              marginBottom: 2,
            }}>
              {day.toLocaleDateString(undefined, { weekday: 'short' })}
            </div>
            <div style={{
              fontSize: 18, fontWeight: 700,
              color: isToday ? '#0891b2' : 'var(--room-text-strong, #0f172a)',
              marginBottom: 8,
            }}>
              {day.getDate()}
            </div>
            <div style={{ flex: 1, overflow: 'auto' }}>
              {dayBots.length === 0 && (
                <div style={{ fontSize: 11, color: 'var(--room-border, #cbd5e1)' }}>—</div>
              )}
              {dayBots.map((b) => (
                <CalendarChip key={b.id} bot={b} onClick={() => onSelect(b.recallBotId)} compact />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function MonthCalendar({ bots, anchorDate, onSelect }) {
  // Build a 6-week grid (42 cells) starting from the Sunday on/before
  // the 1st of the anchor month. Out-of-month days render dimmed so
  // the user sees calendar continuity.
  const monthStart = startOfMonth(anchorDate);
  const gridStart = startOfWeek(monthStart);
  const cells = Array.from({ length: 42 }, (_, i) => {
    const d = new Date(gridStart); d.setDate(gridStart.getDate() + i); return d;
  });
  const today = startOfDay(new Date());
  const currentMonth = monthStart.getMonth();

  return (
    <div>
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)',
        gap: 4, marginBottom: 4,
      }}>
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
          <div key={d} style={{
            fontSize: 10, fontWeight: 700, color: 'var(--room-text-subtle)',
            textTransform: 'uppercase', letterSpacing: '0.05em',
            textAlign: 'center', padding: '4px 0',
          }}>
            {d}
          </div>
        ))}
      </div>
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)',
        gap: 4,
      }}>
        {cells.map((day) => {
          const dayBots = bots.filter((b) => b.meetingStartAt && isSameDay(new Date(b.meetingStartAt), day));
          const isToday = isSameDay(day, today);
          const inMonth = day.getMonth() === currentMonth;
          return (
            <div
              key={day.toISOString()}
              style={{
                background: inMonth ? 'var(--room-card)' : 'var(--room-surface)',
                borderRadius: 8,
                border: `1px solid ${isToday ? '#0891b2' : 'var(--room-border, #e2e8f0)'}`,
                minHeight: 96,
                padding: 6,
                opacity: inMonth ? 1 : 0.55,
                display: 'flex', flexDirection: 'column',
              }}
            >
              <div style={{
                fontSize: 11, fontWeight: 700,
                color: isToday ? '#0891b2' : (inMonth ? 'var(--room-text-strong, #0f172a)' : 'var(--room-text-subtle, #94a3b8)'),
                marginBottom: 4,
                textAlign: 'right',
              }}>
                {day.getDate()}
              </div>
              <div style={{ flex: 1, overflow: 'hidden' }}>
                {dayBots.slice(0, 3).map((b) => (
                  <CalendarChip key={b.id} bot={b} onClick={() => onSelect(b.recallBotId)} compact />
                ))}
                {dayBots.length > 3 && (
                  <div style={{ fontSize: 10, color: 'var(--room-text-muted)', padding: '2px 4px' }}>
                    +{dayBots.length - 3} more
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function UpcomingRow({ bot, onClick, authToken, onChanged, joinEnabled = false }) {
  const isUnconfirmed = !bot.recordingKindConfirmed;
  const isCancelled = bot.status === 'cancelled';
  const isFailed = bot.status === 'failed';
  const [cancelling, setCancelling] = useState(false);
  const [retrying, setRetrying] = useState(false);

  // Failed rows carry the quarantine that stops the poller re-attempting
  // this occurrence. Retry deletes it server-side; the next calendar
  // check (≤10 min) schedules fresh.
  const handleRetry = async (e) => {
    e.stopPropagation();
    if (retrying) return;
    setRetrying(true);
    try {
      const res = await fetch(`${API_BASE}/api/integrations/bots/${bot.id}/retry`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `Retry failed (${res.status})`);
      window.alert(body.message || 'Cleared — will reschedule on the next calendar check.');
      if (typeof onChanged === 'function') onChanged();
    } catch (err) {
      window.alert(`Couldn't retry: ${err.message}`);
    } finally {
      setRetrying(false);
    }
  };
  const kind = bot.recordingKind || 'qualifying_call';
  const kindLabel = kind === 'demo'
    ? 'Demo'
    : kind === 'meeting'
    ? 'Meeting'
    : 'Qualifying Call';
  const kindColor = kind === 'demo'
    ? '#9333ea'
    : kind === 'meeting'
    ? '#6366f1'
    : '#0891b2';

  const handleCancel = async (e) => {
    e.stopPropagation();
    if (cancelling) return;
    const confirmed = window.confirm(
      `Skip recording "${bot.meetingTitle || 'this call'}"?\n\n`
      + 'TrailerCast will not join this meeting. You can still record it manually later by uploading the file.'
    );
    if (!confirmed) return;
    setCancelling(true);
    try {
      const res = await fetch(`${API_BASE}/api/integrations/bots/${bot.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Cancel failed (${res.status})`);
      }
      if (typeof onChanged === 'function') onChanged();
    } catch (err) {
      window.alert(`Couldn't cancel: ${err.message}`);
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div
      onClick={(isCancelled || isFailed) ? undefined : onClick}
      style={{
        background: 'var(--room-card)', borderRadius: 12, padding: '16px 20px',
        border: `1px solid ${isFailed ? '#fecaca' : isCancelled ? 'var(--room-border, #e2e8f0)' : isUnconfirmed ? '#fde68a' : 'var(--room-border, #e2e8f0)'}`,
        cursor: (isCancelled || isFailed) ? 'default' : 'pointer',
        opacity: isCancelled ? 0.55 : 1,
        display: 'flex', alignItems: 'center', gap: 16,
        transition: 'border-color 0.12s ease, box-shadow 0.12s ease, transform 0.12s ease',
      }}
      onMouseEnter={(e) => {
        if (isCancelled || isFailed) return;
        e.currentTarget.style.borderColor = isUnconfirmed ? '#f59e0b' : '#0ea5e9';
        e.currentTarget.style.boxShadow = '0 4px 14px rgba(8, 145, 178, 0.10)';
        e.currentTarget.style.transform = 'translateY(-1px)';
      }}
      onMouseLeave={(e) => {
        if (isCancelled || isFailed) return;
        e.currentTarget.style.borderColor = isUnconfirmed ? '#fde68a' : 'var(--room-border, #e2e8f0)';
        e.currentTarget.style.boxShadow = 'none';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 15, fontWeight: 600, color: 'var(--room-text-strong)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          textDecoration: isCancelled ? 'line-through' : 'none',
        }}>
          {bot.meetingTitle || '(untitled meeting)'}
        </div>
        <div style={{ fontSize: 13, color: 'var(--room-text-muted)', marginTop: 4 }}>
          {fmtMeetingTime(bot.meetingStartAt)}
        </div>
        {isFailed && (
          <div style={{ fontSize: 12, color: '#b91c1c', marginTop: 6, lineHeight: 1.4 }}>
            {bot.error || 'The notetaker couldn’t be scheduled for this meeting.'}
          </div>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {isFailed ? (
          <>
            <span style={{
              fontSize: 11, fontWeight: 700, letterSpacing: '0.05em',
              textTransform: 'uppercase',
              color: '#b91c1c', background: '#fee2e2',
              padding: '4px 10px', borderRadius: 100,
            }}>
              Scheduling failed
            </span>
            <button
              type="button"
              onClick={handleRetry}
              disabled={retrying}
              title="Clear the failure — the notetaker will be rescheduled on the next calendar check (within ~10 minutes)."
              style={{
                padding: '5px 11px', borderRadius: 100,
                border: '1px solid #bae6fd',
                background: retrying ? '#e0f2fe' : 'white',
                color: '#0369a1', cursor: retrying ? 'wait' : 'pointer',
                fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
              }}
            >
              {retrying ? '…' : 'Retry'}
            </button>
          </>
        ) : isCancelled ? (
          <span style={{
            fontSize: 11, fontWeight: 700, letterSpacing: '0.05em',
            textTransform: 'uppercase',
            color: 'var(--room-text-body, #475569)', background: 'var(--room-border-soft, #f1f5f9)',
            padding: '4px 10px', borderRadius: 100,
          }}>
            Notetaker excluded
          </span>
        ) : (
          <>
            {/* Join opens the meeting's own Zoom/Meet/Teams URL in a
                new tab — the same link the calendar app's Join button
                uses. stopPropagation so it doesn't also open the row's
                detail view. Flag-gated (upcoming_calls_join). */}
            {joinEnabled && bot.meetingUrl && (
              <a
                href={bot.meetingUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                title="Open this meeting in a new tab"
                style={{
                  padding: '5px 13px', borderRadius: 100,
                  border: '1px solid #c7d2fe',
                  background: 'var(--room-card, #ffffff)', color: '#4f46e5',
                  fontSize: 12, fontWeight: 700, textDecoration: 'none',
                }}
              >
                Join
              </a>
            )}
            {isUnconfirmed && (
              <span style={{
                fontSize: 11, fontWeight: 700, letterSpacing: '0.05em',
                textTransform: 'uppercase',
                color: '#92400e', background: '#fef3c7',
                padding: '4px 10px', borderRadius: 100,
              }}>
                Needs confirmation
              </span>
            )}
            <span style={{
              fontSize: 12, fontWeight: 600,
              color: kindColor, background: `${kindColor}1a`,
              padding: '4px 12px', borderRadius: 100,
            }}>
              {kindLabel}
            </span>
            {/* Don't-record button — cancels the scheduled bot via
                DELETE /api/integrations/bots/:id. Scheduled-only:
                once the bot is joining/recording the DELETE is a
                guaranteed 409, so don't offer a button that can only
                alert an error. */}
            {bot.status === 'scheduled' && (
            <button
              type="button"
              onClick={handleCancel}
              disabled={cancelling}
              title="Skip recording this call — TrailerCast will not join."
              style={{
                padding: '5px 11px', borderRadius: 100,
                border: '1px solid #fecaca',
                background: cancelling ? '#fee2e2' : 'white',
                color: '#b91c1c', cursor: cancelling ? 'wait' : 'pointer',
                fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
              }}
            >
              {cancelling ? '…' : "Don't record"}
            </button>
            )}
            <span style={{ color: 'var(--room-border, #cbd5e1)', fontSize: 18 }}>›</span>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Completed tab ────────────────────────────────────────────────

// Type pill displayed inline on every completed-row + used as the
// inbox filter at the top of CompletedTab. Keeps colors consistent
// with the rest of the app's recording-kind pills.
const KIND_LABEL = {
  demo:             { label: 'Demo',            color: '#9333ea' },
  qualifying_call:  { label: 'Qualifying Call', color: '#0891b2' },
  meeting:          { label: 'Meeting',         color: '#6366f1' },
};

// Compact "label above input" wrapper for the filter row. Keeps the
// label muted + uppercase so it reads as metadata, not a form prompt.
function FilterField({ label, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <span style={{
        fontSize: 10, fontWeight: 700, letterSpacing: '0.05em',
        textTransform: 'uppercase', color: 'var(--room-text-subtle)',
      }}>{label}</span>
      {children}
    </label>
  );
}

const dateInputStyle = {
  padding: '7px 10px',
  borderRadius: 8,
  border: '1px solid var(--room-border)',
  background: 'var(--room-card)',
  fontSize: 13,
  fontFamily: 'inherit',
  color: 'var(--room-text-strong)',
};

function CompletedTab({ authToken, onSelect, onOpenDemo, isAdmin, isGlobalAdmin }) {
  // Kebab actions need to live at this scope so the dropdown +
  // ReclassifyModal can be a single instance shared across cards.
  const [kebabOpenForCallId, setKebabOpenForCallId] = useState(null);
  const [reclassifyForCall, setReclassifyForCall] = useState(null);
  const [kindFilter, setKindFilter] = useState('all'); // 'all' | 'demo' | 'qualifying_call' | 'meeting'
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  // userScope: 'self' (default) | 'all' | numeric user id (admin only)
  const [userScope, setUserScope] = useState('self');
  // Roster + list loading live in hooks shared with the inbox layout
  // (calls_inbox_layout) so the two screens can never drift on how a
  // recording is fetched or filtered.
  const teamMembers = useTeamMembers(authToken, isAdmin);
  const { calls, loading, error, reload: load } = useRecordings({
    authToken, kindFilter, dateFrom, dateTo, userScope,
  });

  const hasActiveFilters = dateFrom || dateTo || userScope !== 'self' || kindFilter !== 'all';
  const clearFilters = () => {
    setKindFilter('all');
    setDateFrom('');
    setDateTo('');
    setUserScope('self');
  };

  return (
    <div>
      {/* Filter row: date range + user picker (admin only) + clear.
          Applies server-side via /api/recordings query params. */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: 12,
        alignItems: 'center', marginBottom: 12,
      }}>
        <FilterField label="From">
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            style={dateInputStyle}
          />
        </FilterField>
        <FilterField label="To">
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            style={dateInputStyle}
          />
        </FilterField>
        {isAdmin && (
          <FilterField label="Owner">
            <select
              value={String(userScope)}
              onChange={(e) => {
                const v = e.target.value;
                if (v === 'self' || v === 'all') setUserScope(v);
                else setUserScope(Number(v));
              }}
              style={dateInputStyle}
            >
              <option value="self">Just me</option>
              <option value="all">Whole team</option>
              {teamMembers.map((m) => (
                <option key={m.id} value={m.id}>
                  {[m.firstName, m.lastName].filter(Boolean).join(' ') || m.email}
                </option>
              ))}
            </select>
          </FilterField>
        )}
        {hasActiveFilters && (
          <button
            type="button"
            onClick={clearFilters}
            style={{
              alignSelf: 'flex-end',
              padding: '7px 12px', borderRadius: 8,
              border: '1px solid var(--room-border)', background: 'var(--room-card)',
              color: 'var(--room-text-body)', fontSize: 12, fontWeight: 600,
              cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Type filter — applies to the inbox list. Server-side filter,
          not client-side, so the "All" view doesn't pull every demo
          your team has ever done into memory. */}
      <div style={{ display: 'inline-flex', gap: 4, padding: 4, borderRadius: 12, background: 'var(--room-card-hover)', border: '1px solid var(--room-border)', marginBottom: 16 }}>
        {[
          { id: 'all',              label: 'All' },
          { id: 'demo',             label: 'Demos' },
          { id: 'qualifying_call',  label: 'Qualifying' },
          { id: 'meeting',          label: 'Meetings' },
        ].map((opt) => {
          const active = kindFilter === opt.id;
          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => setKindFilter(opt.id)}
              style={{
                padding: '7px 14px', borderRadius: 8, border: 'none',
                background: active ? 'var(--room-card)' : 'transparent',
                color: active ? 'var(--room-text-strong)' : 'var(--room-text-muted)',
                fontWeight: 600, fontSize: 13, fontFamily: 'inherit',
                cursor: 'pointer',
                boxShadow: active ? '0 1px 3px rgba(15, 23, 42, 0.08)' : 'none',
                transition: 'all 0.12s ease',
              }}
            >
              {opt.label}
            </button>
          );
        })}
      </div>

      {loading && <p style={{ color: 'var(--room-text-muted)' }}>Loading…</p>}
      {error && <ErrorBox error={error} />}
      {!loading && !error && calls.length === 0 && (
        <EmptyState
          title={kindFilter === 'all' ? 'No completed calls yet' : `No ${KIND_LABEL[kindFilter] ? `${KIND_LABEL[kindFilter].label}s` : 'recordings'} yet`}
          body="Schedule the bot on a meeting via Integrations, or upload a recording — they'll appear here once processing finishes."
        />
      )}
      {!loading && !error && calls.length > 0 && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
          gap: 16,
        }}>
          {calls.map((call) => (
            <CompletedCard
              key={call.id}
              call={call}
              authToken={authToken}
              isGlobalAdmin={isGlobalAdmin}
              showOwner={userScope !== 'self'}
              kebabOpen={kebabOpenForCallId === call.id}
              onKebabToggle={(open) => setKebabOpenForCallId(open ? call.id : null)}
              onReclassify={() => {
                setKebabOpenForCallId(null);
                setReclassifyForCall(call);
              }}
              onPrivacyChanged={() => load()}
              onClick={() => {
                // Demo recordings have a fully-rendered review surface
                // in the App-level 'completed' phase (player + chapters
                // + Recording Review). Skip the dispatcher entirely
                // and jump straight there. Qualifying + meeting calls
                // open the dispatcher, which renders the type-specific
                // detail variant.
                if (call.recordingKind === 'demo' && call.status === 'completed' && onOpenDemo) {
                  onOpenDemo(call.id);
                } else {
                  onSelect(call.id);
                }
              }}
            />
          ))}
        </div>
      )}

      {/* Reclassify modal — single instance shared by every card's
          kebab. Opens when a card's onReclassify fires. On success
          the list reloads so the kind pill on the row reflects the
          new classification. ReclassifyModal expects the current kind
          + recording id directly (see ReclassifyModal.jsx prop list). */}
      <ReclassifyModal
        open={!!reclassifyForCall}
        authToken={authToken}
        recordingId={reclassifyForCall ? reclassifyForCall.id : null}
        currentKind={reclassifyForCall ? reclassifyForCall.recordingKind : null}
        onClose={() => setReclassifyForCall(null)}
        onReclassified={() => {
          setReclassifyForCall(null);
          load();
        }}
      />
    </div>
  );
}

// Renders the AI-extracted topics array as a clickable chapter list.
// Each topic seeks the video to its startSeconds via onSeek (provided
// by the parent detail component). Empty / null state shows a friendly
// "still processing" or "no topics yet" message.
function TopicsList({ topics, onSeek }) {
  if (!Array.isArray(topics) || topics.length === 0) {
    return (
      <div style={{
        padding: '24px 12px', textAlign: 'center',
        color: 'var(--room-text-muted)', fontSize: 13, lineHeight: 1.6,
      }}>
        <div style={{ fontSize: 28, marginBottom: 8 }}>🗂️</div>
        <strong style={{ color: 'var(--room-text-body)', display: 'block', marginBottom: 4 }}>
          No topics yet
        </strong>
        Topics are extracted as part of the AI summary — they'll appear here once processing finishes.
      </div>
    );
  }
  const fmt = (s) => {
    if (typeof s !== 'number') return '';
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${String(sec).padStart(2, '0')}`;
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {topics.map((t, i) => (
        <button
          key={`topic-${i}`}
          type="button"
          onClick={() => {
            if (typeof t.startSeconds === 'number' && onSeek) onSeek(t.startSeconds);
          }}
          disabled={typeof t.startSeconds !== 'number'}
          style={{
            textAlign: 'left',
            padding: '10px 12px',
            border: '1px solid var(--room-border, #e2e8f0)',
            borderRadius: 10,
            background: 'var(--room-card, #ffffff)',
            cursor: typeof t.startSeconds === 'number' ? 'pointer' : 'default',
            fontFamily: 'inherit',
            transition: 'border-color 120ms ease, background 120ms ease, transform 120ms ease',
          }}
          onMouseEnter={(e) => {
            if (typeof t.startSeconds === 'number') {
              e.currentTarget.style.borderColor = '#0ea5e9';
              e.currentTarget.style.background = '#f0f9ff';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = 'var(--room-border, #e2e8f0)';
            e.currentTarget.style.background = 'var(--room-card, #ffffff)';
          }}
        >
          <div style={{
            display: 'flex', justifyContent: 'space-between', gap: 12,
            alignItems: 'baseline', marginBottom: 2,
          }}>
            <strong style={{ fontSize: 13, color: 'var(--room-text-strong, #0f172a)' }}>{t.label}</strong>
            {typeof t.startSeconds === 'number' && (
              <span style={{
                fontSize: 11, fontWeight: 600,
                color: '#0891b2', flexShrink: 0,
                fontVariantNumeric: 'tabular-nums',
              }}>
                {fmt(t.startSeconds)}
              </span>
            )}
          </div>
          {t.summary && (
            <div style={{ fontSize: 12, color: 'var(--room-text-muted, #64748b)', lineHeight: 1.5 }}>
              {t.summary}
            </div>
          )}
        </button>
      ))}
    </div>
  );
}

// Shared kebab-item style. Block-level button styled like a menu row.
const kebabItemStyle = {
  display: 'block',
  width: '100%',
  textAlign: 'left',
  padding: '8px 14px',
  border: 'none',
  background: 'transparent',
  color: 'var(--room-text-strong)',
  fontSize: 13,
  fontFamily: 'inherit',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

// Thumbnail grid card — replaces the older list-style CompletedRow.
// Shows: 16:9 thumbnail (sprite for demos, gradient placeholder for
// qualifying + meetings until we backfill sprite-gen on those types),
// type pill + status pill on the thumbnail overlay, title (company /
// prospect / filename), AI headline preview, duration + account tags.

// ─── Shared recording helpers (card + inbox row) ──────────────────

// What a recording is called on a card or row: the AI's company read,
// else the prospect, else the file — never a blank tile.
function recordingTitle(call) {
  const summary = call.callSummary || {};
  return summary.company || call.prospectName || call.filename || 'Untitled call';
}

// Non-default state only; an awaiting-review recording shows nothing
// here. Failed used to fall into the processing branch and show a
// perpetual "Processing 0%" — the truth is surfaced instead.
function recordingStatusBadge(call) {
  const isFailed = call.status === 'failed';
  const isProcessing = !isFailed && call.status !== 'completed';
  if (call.disqualifiedAt) return { label: 'Not qualified', bg: 'rgba(107, 114, 128, 0.85)' };
  if (call.buyerSpaceId) return { label: 'In Decision Room', bg: 'rgba(168, 85, 247, 0.85)' };
  if (isFailed) return { label: 'Processing failed', bg: 'rgba(220, 38, 38, 0.85)' };
  if (isProcessing) return { label: `Processing ${call.progress || 0}%`, bg: 'rgba(14, 165, 233, 0.85)' };
  return null;
}

// What length a row or card badges. A demo row opens the composed
// TRAILER, so it badges the trailer's length when one is known;
// everything else (a qualifying call, a meeting, a demo still
// processing) is the recording itself. The founder read "47 min" on a
// 16-minute trailer before this distinction existed.
function displayDurationSeconds(call) {
  const trailer = Number(call.trailerDurationSeconds);
  const isDemo = !call.recordingKind || call.recordingKind === 'demo';
  if (isDemo && Number.isFinite(trailer) && trailer > 0) return trailer;
  const rec = Number(call.durationSeconds);
  return Number.isFinite(rec) && rec > 0 ? rec : null;
}

// Tooltip for the badge: says which length it is, and the other one.
function durationTitle(call) {
  const shown = displayDurationSeconds(call);
  if (!shown) return undefined;
  const trailer = Number(call.trailerDurationSeconds);
  const rec = Number(call.durationSeconds);
  if (shown === trailer && rec > 0 && rec !== trailer) {
    return `Trailer ${fmtCompactDuration(trailer)} · from a ${fmtCompactDuration(rec)} call`;
  }
  return shown === trailer ? 'Trailer length' : 'Recording length';
}

// Team roster for the admin-only owner filter. Non-admins never see the
// dropdown, so nothing is fetched for them.
function useTeamMembers(authToken, isAdmin) {
  const [teamMembers, setTeamMembers] = useState([]);
  useEffect(() => {
    if (!isAdmin) return undefined;
    let cancelled = false;
    fetch(`${API_BASE}/api/company/members`, {
      headers: { Authorization: `Bearer ${authToken}` },
    })
      .then((r) => (r.ok ? r.json() : { members: [] }))
      .then((data) => {
        if (!cancelled) setTeamMembers(Array.isArray(data.members) ? data.members : []);
      })
      .catch(() => { /* roster is best-effort; falls back to "Just me" */ });
    return () => { cancelled = true; };
  }, [isAdmin, authToken]);
  return teamMembers;
}

// The completed-recordings list, filtered server-side. Stale-response
// guarded: rapid filter flips fire overlapping loads ('All' is heavier
// than 'Demos'), and a slower EARLIER response must not land last.
function useRecordings({ authToken, kindFilter, dateFrom, dateTo, userScope }) {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const loadSeqRef = useRef(0);

  const reload = useCallback(() => {
    const seq = ++loadSeqRef.current;
    setLoading(true);
    setError(null);
    const qs = new URLSearchParams();
    if (kindFilter !== 'all') qs.set('kind', kindFilter);
    // Full ISO instants for the USER'S local day boundaries — a bare
    // YYYY-MM-DD parses as UTC midnight on the server, so a call
    // completed 8pm ET on the 2nd fell outside "To: the 2nd".
    if (dateFrom) qs.set('from', new Date(`${dateFrom}T00:00:00`).toISOString());
    if (dateTo) qs.set('to', new Date(`${dateTo}T23:59:59.999`).toISOString());
    if (userScope !== 'self') qs.set('userId', String(userScope));
    const url = qs.toString()
      ? `${API_BASE}/api/recordings?${qs.toString()}`
      : `${API_BASE}/api/recordings`;
    fetch(url, { headers: { Authorization: `Bearer ${authToken}` } })
      .then(async (r) => {
        if (!r.ok) {
          const body = await r.json().catch(() => ({}));
          throw new Error(body.error || `Failed to load (status ${r.status})`);
        }
        return r.json();
      })
      .then((data) => {
        if (seq !== loadSeqRef.current) return; // stale
        setCalls(Array.isArray(data.recordings) ? data.recordings : []);
        setLoading(false);
      })
      .catch((err) => {
        if (seq !== loadSeqRef.current) return;
        setError(err.message);
        setLoading(false);
      });
  }, [authToken, kindFilter, dateFrom, dateTo, userScope]);

  useEffect(() => { reload(); }, [reload]);
  return { calls, loading, error, reload };
}

// Upcoming notetakers. Same endpoint the tabbed Upcoming view uses.
function useUpcomingBots(authToken) {
  const [bots, setBots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const reload = useCallback(() => {
    setLoading(true);
    setError(null);
    fetch(`${API_BASE}/api/qualifying-calls/upcoming`, {
      headers: { Authorization: `Bearer ${authToken}` },
    })
      .then(async (r) => {
        if (!r.ok) {
          const body = await r.json().catch(() => ({}));
          throw new Error(body.error || `Failed to load (status ${r.status})`);
        }
        return r.json();
      })
      .then((data) => {
        setBots(Array.isArray(data.upcoming) ? data.upcoming : []);
        setLoading(false);
      })
      .catch((err) => { setError(err.message); setLoading(false); });
  }, [authToken]);
  useEffect(() => { reload(); }, [reload]);
  return { bots, loading, error, reload };
}

// Unconfirmed first (action required), then soonest.
function sortUpcoming(bots) {
  return [...bots].sort((a, b) => {
    if (a.recordingKindConfirmed !== b.recordingKindConfirmed) {
      return a.recordingKindConfirmed ? 1 : -1;
    }
    return new Date(a.meetingStartAt || 0) - new Date(b.meetingStartAt || 0);
  });
}

// Per-browser preferences, same try/catch posture as the Trailers
// screen: localStorage throws in Safari private mode and on a full
// quota, and an unguarded read inside a useState initializer takes
// the whole render down.
function readPref(key, fallback) {
  try {
    const v = localStorage.getItem(key);
    return v == null ? fallback : v;
  } catch (_) { return fallback; }
}
function writePref(key, value) {
  try { localStorage.setItem(key, value); } catch (_) { /* best effort */ }
}

// ⋮ actions on a recording. Reclassify is available to anyone who can
// see the row; Mark private/public is Global-Admin-only and the backend
// re-checks. One component so the card and the inbox row cannot drift.
function RecordingKebab({
  call, authToken, isGlobalAdmin, open, onToggle, onReclassify, onPrivacyChanged,
}) {
  // Close on any click outside. Capture-phase so a click that ALSO
  // opens another row's menu still closes this one first (the parent
  // decides which stays open). Capture fires BEFORE any bubble-phase
  // stopPropagation inside the menu, so the target is checked here —
  // closing on an internal click unmounted the menu mid-dispatch and
  // swallowed Reclassify/Mark-private clicks.
  const kebabRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => {
      if (kebabRef.current && kebabRef.current.contains(e.target)) return;
      onToggle(false);
    };
    window.addEventListener('click', close, { capture: true });
    return () => window.removeEventListener('click', close, { capture: true });
  }, [open, onToggle]);

  const togglePrivacy = async (e) => {
    e.stopPropagation();
    onToggle(false);
    const next = !call.isPrivate;
    try {
      const res = await fetch(`${API_BASE}/api/demos/${call.id}/privacy`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ isPrivate: next }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Privacy update failed (${res.status})`);
      }
      if (typeof onPrivacyChanged === 'function') onPrivacyChanged();
    } catch (err) {
      window.alert(`Couldn't update privacy: ${err.message}`);
    }
  };

  return (
    <div ref={kebabRef} style={{ position: 'relative' }}>
      <button
        type="button"
        aria-label="Recording actions"
        aria-haspopup="menu"
        aria-expanded={!!open}
        onClick={(e) => { e.stopPropagation(); onToggle(!open); }}
        style={{
          width: 26, height: 26, borderRadius: 6,
          border: '1px solid transparent',
          background: open ? 'var(--room-border, #e2e8f0)' : 'transparent',
          color: 'var(--room-text-muted)',
          cursor: 'pointer', fontSize: 16, lineHeight: 1,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontFamily: 'inherit',
        }}
        onMouseEnter={(e) => { if (!open) e.currentTarget.style.background = 'var(--room-border-soft, #f1f5f9)'; }}
        onMouseLeave={(e) => { if (!open) e.currentTarget.style.background = 'transparent'; }}
      >
        ⋮
      </button>
      {open && (
        <div
          role="menu"
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'absolute', top: 30, right: 0,
            minWidth: 180,
            background: 'var(--room-card, #ffffff)',
            border: '1px solid var(--room-border, #e2e8f0)',
            borderRadius: 8,
            boxShadow: '0 12px 32px rgba(15, 23, 42, 0.14), 0 2px 6px rgba(15, 23, 42, 0.08)',
            padding: '4px 0',
            zIndex: 50,
            textAlign: 'left',
          }}
        >
          <button
            type="button"
            role="menuitem"
            onClick={(e) => { e.stopPropagation(); onReclassify(); }}
            style={kebabItemStyle}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--room-surface, #f8fafc)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            Reclassify…
          </button>
          {isGlobalAdmin && (
            <button
              type="button"
              role="menuitem"
              onClick={togglePrivacy}
              style={kebabItemStyle}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--room-surface, #f8fafc)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              title={call.isPrivate
                ? 'Currently visible only to the owner and you. Click to make it visible to all teammates.'
                : 'Hide this recording from every teammate except the owner and Global Admin (you).'}
            >
              {call.isPrivate ? '🔓 Mark public' : '🔒 Mark private'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Calls inbox (calls_inbox_layout, 2026-09-04) ─────────────────
//
// WHY: the screen opened on "Upcoming" — usually zero or one notetaker
// — and gave it a List/Day/Week/Month calendar with a date navigator,
// while the recordings a rep actually came for sat behind a second
// tab, under two rows of filters, in a card grid that fit about eight
// per screen. The inbox flips that: recordings ARE the page, upcoming
// notetakers are a compact strip above them, and the calendar views
// are gone (the sidebar widget and the rep's own calendar already
// answer "what's on today").

function CallsInbox({ authToken, onSelect, onOpenDemo, authUser, setPhase }) {
  const isAdmin = !!(authUser && authUser.company && authUser.company.isAdmin);
  const isGlobalAdmin = !!(authUser && authUser.company && authUser.company.isGlobalAdmin);
  const joinEnabled = !!authUser?.company?.featureFlags?.upcoming_calls_join;
  return (
    <div style={{ marginTop: 24 }}>
      <UpNextStrip
        authToken={authToken}
        onSelect={onSelect}
        joinEnabled={joinEnabled}
        setPhase={setPhase}
      />
      <RecordingsInbox
        authToken={authToken}
        onSelect={onSelect}
        onOpenDemo={onOpenDemo}
        isAdmin={isAdmin}
        isGlobalAdmin={isGlobalAdmin}
      />
    </div>
  );
}

// Upcoming notetakers as one row of chips. "Manage" expands to the full
// rows (Don't record / Retry / confirm) — the actions survive, the
// calendar does not. Nothing scheduled collapses to a single line.
function UpNextStrip({ authToken, onSelect, joinEnabled, setPhase }) {
  const { bots, loading, error, reload } = useUpcomingBots(authToken);
  const [expanded, setExpanded] = useState(false);
  const sorted = useMemo(() => sortUpcoming(bots), [bots]);
  // Chips show what is still going to happen; an excluded notetaker is
  // only worth a row when managing.
  const chips = sorted.filter((b) => b.status !== 'cancelled');
  const needsConfirmation = chips.filter((b) => !b.recordingKindConfirmed && b.status !== 'failed').length;

  const lineStyle = { fontSize: 13, color: 'var(--room-text-muted)', margin: '0 0 20px' };
  if (loading) return <p style={lineStyle}>Checking upcoming calls…</p>;
  if (error) return <p style={{ ...lineStyle, color: '#dc2626' }}>Couldn't load upcoming calls: {error}</p>;
  if (sorted.length === 0) {
    return (
      <p style={lineStyle}>
        No notetakers scheduled.{' '}
        <button
          type="button"
          onClick={() => setPhase && setPhase('integrations')}
          style={{
            border: 'none', background: 'none', padding: 0, cursor: 'pointer',
            color: '#0891b2', fontWeight: 600, fontSize: 13, fontFamily: 'inherit',
          }}
        >
          Schedule one from Integrations
        </button>
      </p>
    );
  }

  return (
    <section style={{ marginBottom: 24 }} aria-label="Upcoming calls">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
        <span style={{
          fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
          color: 'var(--room-text-subtle, #94a3b8)',
        }}>
          Up next
        </span>
        <span style={{ fontSize: 12, color: 'var(--room-text-muted)' }}>
          {chips.length} scheduled
        </span>
        {needsConfirmation > 0 && (
          <span style={{
            fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase',
            color: '#92400e', background: '#fef3c7', padding: '3px 9px', borderRadius: 100,
          }}>
            {needsConfirmation} need{needsConfirmation === 1 ? 's' : ''} confirmation
          </span>
        )}
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          style={{
            marginLeft: 'auto', border: '1px solid var(--room-border, #e2e8f0)',
            background: 'var(--room-card, #fff)', color: 'var(--room-text-body, #475569)',
            padding: '5px 12px', borderRadius: 100, fontSize: 12, fontWeight: 600,
            cursor: 'pointer', fontFamily: 'inherit',
          }}
        >
          {expanded ? 'Collapse' : `Manage${sorted.length !== chips.length ? ` (${sorted.length})` : ''}`}
        </button>
      </div>

      {expanded ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {sorted.map((bot) => (
            <UpcomingRow
              key={bot.id}
              bot={bot}
              authToken={authToken}
              onChanged={reload}
              onClick={() => onSelect(bot.recallBotId)}
              joinEnabled={joinEnabled}
            />
          ))}
        </div>
      ) : (
        <div style={{
          display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 4,
          scrollbarWidth: 'thin',
        }}>
          {chips.map((bot) => (
            <UpNextChip
              key={bot.id}
              bot={bot}
              joinEnabled={joinEnabled}
              onClick={() => onSelect(bot.recallBotId)}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function UpNextChip({ bot, joinEnabled, onClick }) {
  const isFailed = bot.status === 'failed';
  const isUnconfirmed = !bot.recordingKindConfirmed && !isFailed;
  const kind = bot.recordingKind || 'qualifying_call';
  const meta = KIND_LABEL[kind] || KIND_LABEL.qualifying_call;
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={isFailed ? undefined : onClick}
      onKeyDown={(e) => {
        if (isFailed) return;
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); }
      }}
      title={isFailed ? (bot.error || 'The notetaker couldn’t be scheduled.') : 'Open this call'}
      style={{
        flex: '0 0 auto', width: 260, minWidth: 0,
        display: 'flex', flexDirection: 'column', gap: 6,
        padding: '10px 12px', borderRadius: 12,
        background: 'var(--room-card)',
        border: `1px solid ${isFailed ? '#fecaca' : isUnconfirmed ? '#fde68a' : 'var(--room-border, #e2e8f0)'}`,
        cursor: isFailed ? 'default' : 'pointer',
        transition: 'border-color 0.12s ease, box-shadow 0.12s ease',
      }}
      onMouseEnter={(e) => {
        if (isFailed) return;
        e.currentTarget.style.borderColor = isUnconfirmed ? '#f59e0b' : '#0ea5e9';
        e.currentTarget.style.boxShadow = '0 4px 14px rgba(8, 145, 178, 0.10)';
      }}
      onMouseLeave={(e) => {
        if (isFailed) return;
        e.currentTarget.style.borderColor = isUnconfirmed ? '#fde68a' : 'var(--room-border, #e2e8f0)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      <div style={{
        fontSize: 13, fontWeight: 600, color: 'var(--room-text-strong)',
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {bot.meetingTitle || '(untitled meeting)'}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--room-text-muted)', minWidth: 0 }}>
        <span style={{ whiteSpace: 'nowrap' }}>{fmtMeetingTime(bot.meetingStartAt)}</span>
        <span style={{
          fontSize: 10, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase',
          color: meta.color, background: `${meta.color}1a`, padding: '2px 7px', borderRadius: 100,
          whiteSpace: 'nowrap',
        }}>
          {meta.label === 'Qualifying Call' ? 'Qualifying' : meta.label}
        </span>
        {isUnconfirmed && (
          <span
            title="Needs confirmation — open to confirm the call type"
            style={{ width: 8, height: 8, borderRadius: 100, background: '#f59e0b', flexShrink: 0 }}
          />
        )}
        {isFailed && (
          <span style={{ fontSize: 10, fontWeight: 700, color: '#b91c1c', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Failed
          </span>
        )}
        {joinEnabled && bot.meetingUrl && !isFailed && (
          <a
            href={bot.meetingUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            title="Open this meeting in a new tab"
            style={{
              marginLeft: 'auto', padding: '3px 10px', borderRadius: 100,
              border: '1px solid #c7d2fe', background: 'var(--room-card, #fff)',
              color: '#4f46e5', fontSize: 11, fontWeight: 700, textDecoration: 'none',
              whiteSpace: 'nowrap',
            }}
          >
            Join
          </a>
        )}
      </div>
    </div>
  );
}

// Every completed recording, one toolbar, list by default.
function RecordingsInbox({ authToken, onSelect, onOpenDemo, isAdmin, isGlobalAdmin }) {
  const [kindFilter, setKindFilter] = useState(() => {
    const v = readPref('trailercst_calls_kind', 'all');
    return ['all', 'demo', 'qualifying_call', 'meeting'].includes(v) ? v : 'all';
  });
  const [view, setView] = useState(() => (readPref('trailercst_calls_view', 'list') === 'grid' ? 'grid' : 'list'));
  const [query, setQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [userScope, setUserScope] = useState('self');
  const [kebabOpenForCallId, setKebabOpenForCallId] = useState(null);
  const [reclassifyForCall, setReclassifyForCall] = useState(null);

  useEffect(() => { writePref('trailercst_calls_kind', kindFilter); }, [kindFilter]);
  useEffect(() => { writePref('trailercst_calls_view', view); }, [view]);

  const teamMembers = useTeamMembers(authToken, isAdmin);
  const { calls, loading, error, reload } = useRecordings({
    authToken, kindFilter, dateFrom, dateTo, userScope,
  });

  // Search is client-side over the loaded page: title, prospect, file,
  // AI headline, owner. The list endpoint has no text search yet; when
  // it grows one this moves server-side like the Trailers box did.
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return calls;
    return calls.filter((c) => {
      const hay = [
        recordingTitle(c), c.prospectName, c.filename,
        c.callSummary && c.callSummary.headline, c.ownerName, c.ownerEmail,
      ].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [calls, query]);

  const hasActiveFilters = !!(dateFrom || dateTo || userScope !== 'self' || kindFilter !== 'all' || query.trim());
  const clearFilters = () => {
    setKindFilter('all'); setDateFrom(''); setDateTo(''); setUserScope('self'); setQuery('');
  };

  const openCall = (call) => {
    // Demo recordings have a fully-rendered review surface in the
    // App-level 'completed' phase; everything else opens the
    // type-specific detail via the dispatcher.
    if (call.recordingKind === 'demo' && call.status === 'completed' && onOpenDemo) {
      onOpenDemo(call.id);
    } else {
      onSelect(call.id);
    }
  };

  const segment = (active) => ({
    padding: '7px 14px', borderRadius: 8, border: 'none',
    background: active ? 'var(--room-card)' : 'transparent',
    color: active ? 'var(--room-text-strong)' : 'var(--room-text-muted)',
    fontWeight: 600, fontSize: 13, fontFamily: 'inherit', cursor: 'pointer',
    boxShadow: active ? '0 1px 3px rgba(15, 23, 42, 0.08)' : 'none',
    transition: 'all 0.12s ease', whiteSpace: 'nowrap',
  });
  const segmentGroup = {
    display: 'inline-flex', gap: 4, padding: 4, borderRadius: 12,
    background: 'var(--room-card-hover)', border: '1px solid var(--room-border)',
  };

  const showOwner = userScope !== 'self';

  return (
    <div>
      <style>{`
        .calls-inbox-toolbar { display: flex; flex-wrap: wrap; align-items: center; gap: 10px; margin-bottom: 14px; }
        .calls-inbox-search { flex: 1 1 220px; min-width: 180px; max-width: 420px; }
        .calls-inbox-search input { width: 100%; box-sizing: border-box; }
        .calls-inbox-right { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-left: auto; }
        .calls-list { border: 1px solid var(--room-border, #e2e8f0); border-radius: 14px; overflow: hidden; background: var(--room-card); }
        .calls-list-head, .calls-list-row {
          display: grid;
          grid-template-columns: minmax(0, 1fr) 118px 120px 76px 150px 36px;
          align-items: center; gap: 12px; padding: 10px 14px;
        }
        .calls-list-head {
          font-size: 11px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase;
          color: var(--room-text-subtle, #94a3b8); background: var(--room-surface, #f8fafc);
        }
        .calls-list-row { border-top: 1px solid var(--room-border-soft, #f1f5f9); cursor: pointer; transition: background 0.12s ease; }
        .calls-list-row:hover { background: var(--room-card-hover, #f8fafc); }
        .calls-list-row:focus-visible { outline: 2px solid rgba(14, 165, 233, 0.55); outline-offset: -2px; }
        .calls-cell-muted { font-size: 12px; color: var(--room-text-muted); font-variant-numeric: tabular-nums; white-space: nowrap; }
        @media (max-width: 900px) {
          .calls-list-head, .calls-list-row { grid-template-columns: minmax(0, 1fr) 100px 36px; }
          .calls-cell-date, .calls-cell-length, .calls-cell-status { display: none; }
        }
      `}</style>

      <div className="calls-inbox-toolbar">
        <div style={segmentGroup} role="tablist" aria-label="Recording type">
          {[
            { id: 'all',             label: 'All' },
            { id: 'demo',            label: 'Demos' },
            { id: 'qualifying_call', label: 'Qualifying' },
            { id: 'meeting',         label: 'Meetings' },
          ].map((opt) => (
            <button
              key={opt.id}
              type="button"
              role="tab"
              aria-selected={kindFilter === opt.id}
              onClick={() => setKindFilter(opt.id)}
              style={segment(kindFilter === opt.id)}
            >
              {opt.label}
            </button>
          ))}
        </div>

        <div className="calls-inbox-search">
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search company, prospect, or headline…"
            aria-label="Search recordings"
            style={dateInputStyle}
          />
        </div>

        <div className="calls-inbox-right">
          {isAdmin && (
            <select
              value={String(userScope)}
              onChange={(e) => {
                const v = e.target.value;
                if (v === 'self' || v === 'all') setUserScope(v);
                else setUserScope(Number(v));
              }}
              aria-label="Owner"
              style={dateInputStyle}
            >
              <option value="self">Just me</option>
              <option value="all">Whole team</option>
              {teamMembers.map((m) => (
                <option key={m.id} value={m.id}>
                  {[m.firstName, m.lastName].filter(Boolean).join(' ') || m.email}
                </option>
              ))}
            </select>
          )}
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} aria-label="From date" title="From" style={dateInputStyle} />
          <span style={{ color: 'var(--room-text-subtle, #94a3b8)', fontSize: 12 }}>to</span>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} aria-label="To date" title="To" style={dateInputStyle} />
          <div style={segmentGroup} role="group" aria-label="Layout">
            <button type="button" onClick={() => setView('list')} style={segment(view === 'list')} aria-pressed={view === 'list'} title="List">List</button>
            <button type="button" onClick={() => setView('grid')} style={segment(view === 'grid')} aria-pressed={view === 'grid'} title="Grid">Grid</button>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, fontSize: 12, color: 'var(--room-text-muted)' }}>
        {!loading && !error && (
          <span>
            {visible.length === calls.length
              ? `${calls.length} recording${calls.length === 1 ? '' : 's'}`
              : `${visible.length} of ${calls.length} recordings match`}
          </span>
        )}
        {hasActiveFilters && (
          <button
            type="button"
            onClick={clearFilters}
            style={{
              border: 'none', background: 'none', padding: 0, cursor: 'pointer',
              color: '#0891b2', fontWeight: 600, fontSize: 12, fontFamily: 'inherit',
            }}
          >
            Clear filters
          </button>
        )}
      </div>

      {loading && <p style={{ color: 'var(--room-text-muted)' }}>Loading…</p>}
      {error && <ErrorBox error={error} />}
      {!loading && !error && calls.length === 0 && (
        <EmptyState
          title={kindFilter === 'all' ? 'No recordings yet' : `No ${KIND_LABEL[kindFilter] ? `${KIND_LABEL[kindFilter].label}s` : 'recordings'} yet`}
          body="Schedule the notetaker on a meeting via Integrations, or upload a recording — it appears here once processing finishes."
        />
      )}
      {!loading && !error && calls.length > 0 && visible.length === 0 && (
        <EmptyState
          title="Nothing matches that search"
          body="Try a company name, a prospect, or a word from the call's headline."
        />
      )}

      {!loading && !error && visible.length > 0 && view === 'grid' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 }}>
          {visible.map((call) => (
            <CompletedCard
              key={call.id}
              call={call}
              authToken={authToken}
              isGlobalAdmin={isGlobalAdmin}
              showOwner={showOwner}
              kebabOpen={kebabOpenForCallId === call.id}
              onKebabToggle={(open) => setKebabOpenForCallId(open ? call.id : null)}
              onReclassify={() => { setKebabOpenForCallId(null); setReclassifyForCall(call); }}
              onPrivacyChanged={() => reload()}
              onClick={() => openCall(call)}
            />
          ))}
        </div>
      )}

      {!loading && !error && visible.length > 0 && view === 'list' && (
        <div className="calls-list" role="table" aria-label="Recordings">
          <div className="calls-list-head" role="row">
            <span role="columnheader">Recording</span>
            <span role="columnheader">Type</span>
            <span role="columnheader" className="calls-cell-date">Date</span>
            <span role="columnheader" className="calls-cell-length">Length</span>
            <span role="columnheader" className="calls-cell-status">{showOwner ? 'Owner' : 'Status'}</span>
            <span role="columnheader" aria-label="Actions" />
          </div>
          {visible.map((call) => (
            <CompletedRow
              key={call.id}
              call={call}
              authToken={authToken}
              isGlobalAdmin={isGlobalAdmin}
              showOwner={showOwner}
              kebabOpen={kebabOpenForCallId === call.id}
              onKebabToggle={(open) => setKebabOpenForCallId(open ? call.id : null)}
              onReclassify={() => { setKebabOpenForCallId(null); setReclassifyForCall(call); }}
              onPrivacyChanged={() => reload()}
              onClick={() => openCall(call)}
            />
          ))}
        </div>
      )}

      <ReclassifyModal
        open={!!reclassifyForCall}
        authToken={authToken}
        recordingId={reclassifyForCall ? reclassifyForCall.id : null}
        currentKind={reclassifyForCall ? reclassifyForCall.recordingKind : null}
        onClose={() => setReclassifyForCall(null)}
        onReclassified={() => { setReclassifyForCall(null); reload(); }}
      />
    </div>
  );
}

// One recording as a dense row: thumbnail, title + AI headline, type,
// date, length, status or owner, ⋮. Same data and actions as the card.
function CompletedRow({
  call, onClick, authToken, isGlobalAdmin, showOwner,
  kebabOpen, onKebabToggle, onReclassify, onPrivacyChanged,
}) {
  const summary = call.callSummary || {};
  const title = recordingTitle(call);
  const initial = (title || '?').trim().charAt(0).toUpperCase();
  const kindMeta = KIND_LABEL[call.recordingKind] || { label: call.recordingKind || 'Call', color: 'var(--room-text-muted)' };
  const status = recordingStatusBadge(call);
  const hasSprite = !!call.thumbnailSpriteKey;

  return (
    <div
      className="calls-list-row"
      role="row"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
    >
      <div role="cell" style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
        <div style={{
          flexShrink: 0, width: 88, aspectRatio: '16 / 9', borderRadius: 6, overflow: 'hidden',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          ...(hasSprite ? {
            backgroundImage: `url(${API_BASE}/api/thumbnails/${encodeURIComponent(call.id)})`,
            backgroundSize: '1000% 100%',
            backgroundPosition: '50% 0',
            backgroundRepeat: 'no-repeat',
          } : {
            background: `linear-gradient(135deg, ${kindMeta.color}, ${kindMeta.color}99)`,
          }),
        }}>
          {!hasSprite && (
            <span style={{ fontSize: 20, fontWeight: 800, color: '#fff', letterSpacing: '-0.02em' }}>{initial}</span>
          )}
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{
            fontSize: 14, fontWeight: 600, color: 'var(--room-text-strong)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            display: 'flex', alignItems: 'center', gap: 6,
          }}>
            {call.isPrivate && (
              <span title="Private — visible only to the owner and Global Admin" aria-label="Private recording" style={{ color: '#6d28d9', fontSize: 12, lineHeight: 1, flexShrink: 0 }}>🔒</span>
            )}
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</span>
            {call.prospectName && call.prospectName !== title && (
              <span style={{
                flexShrink: 0, fontSize: 10, fontWeight: 700, letterSpacing: '0.03em',
                color: '#0c4a6e', background: '#e0f2fe', padding: '2px 7px', borderRadius: 100,
              }}>
                {call.prospectName}
              </span>
            )}
          </div>
          {summary.headline && (
            <div style={{
              fontSize: 12, color: 'var(--room-text-muted)', marginTop: 2,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {summary.headline}
            </div>
          )}
        </div>
      </div>

      <div role="cell">
        <span style={{
          fontSize: 10, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase',
          color: kindMeta.color, background: `${kindMeta.color}1a`, padding: '3px 8px', borderRadius: 100,
          whiteSpace: 'nowrap',
        }}>
          {kindMeta.label === 'Qualifying Call' ? 'Qualifying' : kindMeta.label}
        </span>
      </div>

      <div role="cell" className="calls-cell-muted calls-cell-date">{fmtDate(call.completedAt || call.createdAt)}</div>

      <div role="cell" className="calls-cell-muted calls-cell-length" title={durationTitle(call)}>
        {displayDurationSeconds(call) ? fmtCompactDuration(displayDurationSeconds(call)) : '—'}
      </div>

      <div role="cell" className="calls-cell-status" style={{ minWidth: 0 }}>
        {showOwner ? (
          <span className="calls-cell-muted" style={{ overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>
            {call.ownerName || call.ownerEmail || '—'}
          </span>
        ) : status ? (
          <span style={{
            fontSize: 10, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase',
            padding: '3px 8px', borderRadius: 100, color: '#fff', background: status.bg, whiteSpace: 'nowrap',
          }}>
            {status.label}
          </span>
        ) : (
          <span className="calls-cell-muted">Ready</span>
        )}
      </div>

      <div role="cell" onClick={(e) => e.stopPropagation()}>
        <RecordingKebab
          call={call}
          authToken={authToken}
          isGlobalAdmin={isGlobalAdmin}
          open={kebabOpen}
          onToggle={onKebabToggle}
          onReclassify={onReclassify}
          onPrivacyChanged={onPrivacyChanged}
        />
      </div>
    </div>
  );
}

function CompletedCard({
  call,
  onClick,
  authToken,
  isGlobalAdmin,
  showOwner,
  kebabOpen,
  onKebabToggle,
  onReclassify,
  onPrivacyChanged,
}) {
  const isPromoted = Boolean(call.buyerSpaceId);
  const summary = call.callSummary || {};
  const companyLabel = recordingTitle(call);
  const initial = (companyLabel || '?').trim().charAt(0).toUpperCase();
  const kindMeta = KIND_LABEL[call.recordingKind] || { label: call.recordingKind || 'Call', color: 'var(--room-text-muted)' };

  // Status pill — only surfaces non-default states; an awaiting-review
  // card just shows the type pill. Shared with the inbox list row.
  const statusOverlay = recordingStatusBadge(call);

  // Thumbnail: sprite for any recording with one (currently demos
  // generate sprites; qualifying + meetings fall through to the
  // gradient placeholder below). Future: backfill sprite-gen against
  // the source recording for non-demo kinds.
  const hasSprite = !!call.thumbnailSpriteKey;

  return (
    <div
      onClick={onClick}
      style={{
        background: 'var(--room-card)', borderRadius: 14, overflow: 'hidden',
        border: '1px solid var(--room-border)', cursor: 'pointer',
        display: 'flex', flexDirection: 'column',
        transition: 'transform 0.12s ease, box-shadow 0.12s ease, border-color 0.12s ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = '#0ea5e9';
        e.currentTarget.style.boxShadow = '0 6px 20px rgba(8, 145, 178, 0.12)';
        e.currentTarget.style.transform = 'translateY(-2px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = 'var(--room-border, #e2e8f0)';
        e.currentTarget.style.boxShadow = 'none';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      {/* Thumbnail. Sprite path: the sprite is a 10-frame horizontal
          strip (one row, 10 tiles), so we render via background-size
          1000% × 100% to make one tile span the container, and pin
          background-position-x to the middle frame as a poster. The
          full hover-scrub treatment is in App.jsx's ReelThumb; we
          could lift that in here later if a static poster isn't
          enough. */}
      <div style={{
        position: 'relative',
        aspectRatio: '16 / 9',
        ...(hasSprite ? {
          backgroundImage: `url(${API_BASE}/api/thumbnails/${encodeURIComponent(call.id)})`,
          backgroundSize: '1000% 100%',
          backgroundPosition: '50% 0',
          backgroundRepeat: 'no-repeat',
        } : {
          background: `linear-gradient(135deg, ${kindMeta.color}, ${kindMeta.color}99)`,
        }),
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {!hasSprite && (
          <span style={{
            fontSize: 56, fontWeight: 800, color: '#fff',
            letterSpacing: '-0.02em',
            textShadow: '0 4px 12px rgba(15, 23, 42, 0.25)',
          }}>
            {initial}
          </span>
        )}
        {/* Type pill (top-left) */}
        <span style={{
          position: 'absolute', top: 8, left: 8,
          fontSize: 10, fontWeight: 700, letterSpacing: '0.04em',
          textTransform: 'uppercase',
          padding: '3px 8px', borderRadius: 100,
          color: '#fff', background: kindMeta.color,
          boxShadow: '0 2px 6px rgba(15, 23, 42, 0.18)',
        }}>
          {kindMeta.label}
        </span>
        {/* Status overlay (top-right) */}
        {statusOverlay && (
          <span style={{
            position: 'absolute', top: 8, right: 8,
            fontSize: 10, fontWeight: 700, letterSpacing: '0.04em',
            textTransform: 'uppercase',
            padding: '3px 8px', borderRadius: 100,
            color: '#fff', background: statusOverlay.bg,
            boxShadow: '0 2px 6px rgba(15, 23, 42, 0.18)',
          }}>
            {statusOverlay.label}
          </span>
        )}
        {/* Duration (bottom-right) — the trailer's on a demo card */}
        {displayDurationSeconds(call) && (
          <span title={durationTitle(call)} style={{
            position: 'absolute', bottom: 8, right: 8,
            fontSize: 11, fontWeight: 700,
            padding: '3px 8px', borderRadius: 6,
            color: '#fff', background: 'rgba(15, 23, 42, 0.78)',
            fontVariantNumeric: 'tabular-nums',
          }}>
            {fmtCompactDuration(displayDurationSeconds(call))}
          </span>
        )}
      </div>

      {/* Body */}
      <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 6, minHeight: 90, position: 'relative' }}>
        <div style={{
          fontSize: 14, fontWeight: 700, color: 'var(--room-text-strong)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          paddingRight: 28,
          display: 'flex', alignItems: 'center', gap: 6,
        }}>
          {/* Lock indicator on private rows the caller is allowed to see.
              Shown ahead of the title so the icon reads as metadata,
              not text decoration. */}
          {call.isPrivate && (
            <span
              title="Private — visible only to the owner and Global Admin"
              aria-label="Private recording"
              style={{ color: '#6d28d9', fontSize: 13, lineHeight: 1, flexShrink: 0 }}
            >
              🔒
            </span>
          )}
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {companyLabel}
          </span>
        </div>

        {/* Kebab menu — actions on this recording. Shared with the
            inbox list row (RecordingKebab). */}
        <div style={{ position: 'absolute', top: 8, right: 8 }}>
          <RecordingKebab
            call={call}
            authToken={authToken}
            isGlobalAdmin={isGlobalAdmin}
            open={kebabOpen}
            onToggle={onKebabToggle}
            onReclassify={onReclassify}
            onPrivacyChanged={onPrivacyChanged}
          />
        </div>
        <div style={{
          fontSize: 12, color: 'var(--room-text-muted)',
          fontVariantNumeric: 'tabular-nums',
        }}>
          {fmtDate(call.completedAt || call.createdAt)}
          {/* Owner caption — only in cross-rep views ("Whole team" /
              a specific teammate) so admins can tell whose call each
              card is. The backend JOINs users for exactly this. */}
          {showOwner && (call.ownerName || call.ownerEmail) && (
            <span style={{ fontStyle: 'italic' }}>
              {' · by '}{call.ownerName || call.ownerEmail}
            </span>
          )}
        </div>
        {summary.headline && (
          <div style={{
            fontSize: 12, color: 'var(--room-text-body)', lineHeight: 1.4,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}>
            {summary.headline}
          </div>
        )}
        {/* Account tags row */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 'auto' }}>
          {call.prospectName && (
            <span style={{
              fontSize: 10, fontWeight: 700, letterSpacing: '0.03em',
              color: '#0c4a6e', background: '#e0f2fe',
              padding: '3px 8px', borderRadius: 100,
            }}>
              {call.prospectName}
            </span>
          )}
          {isPromoted && (
            <span style={{
              fontSize: 10, fontWeight: 700, letterSpacing: '0.03em',
              color: '#6d28d9', background: '#f3e8ff',
              padding: '3px 8px', borderRadius: 100,
            }}>
              Decision Room
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// Compact duration: "23 min" / "1 h 12 m" / "0:42 sec".
function fmtCompactDuration(seconds) {
  if (typeof seconds !== 'number' || !Number.isFinite(seconds)) return '';
  const total = Math.max(0, Math.round(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m >= 1) return `${m} min`;
  return `${total}s`;
}

// ─── Detail view ────────────────────────────────────────────────

function CallDetailDispatcher({ authToken, callId, onBack, setPhase, onOpenDemo, authUser }) {
  // Hooks first — this component has early returns for the loading and
  // error states below, and a hook after an early return is a
  // rules-of-hooks violation the build rejects.
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  // Loads the recording via the unified /api/recordings endpoint and
  // dispatches to the right detail UI based on recording_kind:
  //   qualifying_call → CompletedDetail (qualifying-shaped summary)
  //   meeting         → MeetingDetail (generic summary)
  // Demo clicks are handled directly at the inbox level (CompletedCard
  // fires onOpenDemo straight to the App-level 'completed' phase).
  // We still handle 'demo' here as a fallback for direct hash links
  // (e.g. someone bookmarked #/calls/<demoId>) by showing a small
  // "Opening trailer review…" stub + back button.
  // Falls back to /api/qualifying-calls/upcoming for the
  // recall-bot-id case (a click on an upcoming row before the bot
  // has fired yet).
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState(null); // 'upcoming' | 'completed'
  // Signed playback token — /api/source-stream REQUIRES it for private
  // recordings (mint is policy-checked server-side). Best-effort for
  // public rows, mandatory for private ones.
  const [playToken, setPlayToken] = useState(null);
  // Stale-response guard: navigating between calls while a slow detail
  // fetch is in flight re-creates load() with the new callId; without
  // this, the OLD response could resolve last and overwrite the newer
  // call's data.
  const loadSeqRef = useRef(0);

  const load = useCallback(async () => {
    const seq = ++loadSeqRef.current;
    const isCurrent = () => seq === loadSeqRef.current;
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(`${API_BASE}/api/recordings/${encodeURIComponent(callId)}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (r.ok) {
        const json = await r.json();
        // An OK response without the expected payload used to fall
        // through to `if (!data) return null` — a blank page with no
        // back button. Treat it as an error instead.
        if (!json || !json.recording) throw new Error('Call not found');
        if (!isCurrent()) return;
        setData(json.recording);
        setMode('completed');
        // Private rows need the signed token before source-stream will
        // serve them. Fire-and-forget; the <video> src re-renders when
        // the token lands.
        if (json.recording.isPrivate) {
          fetch(`${API_BASE}/api/demos/${encodeURIComponent(json.recording.id)}/playback-token`, {
            headers: { Authorization: `Bearer ${authToken}` },
          })
            .then((tr) => (tr.ok ? tr.json() : null))
            .then((td) => {
              if (td && td.token && isCurrent()) setPlayToken(td.token);
            })
            .catch(() => { /* non-fatal — player shows its error state */ });
        } else {
          setPlayToken(null);
        }
      } else if (r.status === 404) {
        // Upcoming-bot fallback (recall_bot_id, not a demo id).
        const u = await fetch(`${API_BASE}/api/qualifying-calls/upcoming`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (!u.ok) throw new Error('Call not found');
        const upcomingJson = await u.json();
        const bot = (upcomingJson.upcoming || []).find((b) => b.recallBotId === callId);
        if (!bot) throw new Error('Call not found');
        if (!isCurrent()) return;
        setData(bot);
        setMode('upcoming');
      } else {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.error || `Failed to load (status ${r.status})`);
      }
    } catch (err) {
      if (seq === loadSeqRef.current) setError(err.message);
    } finally {
      if (seq === loadSeqRef.current) setLoading(false);
    }
  }, [authToken, callId]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: 'var(--room-text-muted)' }}>
        Loading…
      </div>
    );
  }
  if (error) {
    return (
      <div style={{ padding: 40, maxWidth: 600, margin: '0 auto' }}>
        <BackButton onClick={onBack} />
        <ErrorBox error={error} />
      </div>
    );
  }
  if (!data) return null;

  // Demo fallback: the inbox-level shortcut handles demos in the
  // common case, but a direct hash link to #/calls/<demoId> lands
  // here. Show a minimal redirect-style stub so the user can navigate
  // via the back button or click through to the trailer review.
  if (mode === 'completed' && data.recordingKind === 'demo') {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--room-surface)' }}>
        <div style={{ maxWidth: 720, margin: '0 auto', padding: '40px 24px' }}>
          <BackButton onClick={onBack} />
          <div style={{
            background: 'var(--room-card)', borderRadius: 14, padding: 28,
            border: '1px solid var(--room-border)', textAlign: 'center',
          }}>
            <h2 style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 700, color: 'var(--room-text-strong)' }}>
              This is a demo recording
            </h2>
            <p style={{ margin: '0 0 16px', color: 'var(--room-text-muted)', fontSize: 14 }}>
              The full trailer-review surface lives in the Library / Trailer
              view — open it there to play, rebuild clips, or share.
            </p>
            <button
              type="button"
              onClick={() => {
                // onOpenDemo (App's loadCompletedDemo) actually FETCHES
                // the demo and flips the phase. The old hash+setPhase
                // approach relied on a restore effect that only runs
                // once per session from the dashboard — so this button
                // rendered a blank player (or a previously viewed
                // trailer) instead of this demo.
                if (typeof onOpenDemo === 'function') {
                  onOpenDemo(data.id);
                } else {
                  setPhase('completed');
                }
              }}
              style={{
                padding: '10px 20px', borderRadius: 10, border: 'none',
                background: 'linear-gradient(135deg, #0891b2 0%, #0ea5e9 50%, #6366f1 100%)',
                color: '#fff', fontFamily: 'inherit', fontSize: 14, fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Open trailer review →
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Delete (2026-08-28, flag: calls_delete). The backend has supported
  // deleting any recording all along — real deletion, R2 cleanup,
  // shared-source guard — but the only Delete button in the app lived
  // on the Trailers screen, and meetings never appear there. So a
  // mis-recorded meeting was deletable by API and undeletable by a
  // human. First passenger on the Friday release train: on for the
  // dogfood tenant immediately, dark for customers until the flip.
  const canDelete = mode !== 'upcoming' && data
    && !!authUser?.company?.featureFlags?.calls_delete;
  const deleteCall = async () => {
    const label = data?.filename || data?.prospectName || 'this call';
    if (!window.confirm(
      `Delete "${label}"?

This removes the recording, transcript, AI summary and any share links. It can't be undone.`
    )) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      const r = await fetch(`${API_BASE}/api/demos/${encodeURIComponent(data.id)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.error || `status ${r.status}`);
      }
      onBack();
    } catch (err) {
      setDeleteError(err.message);
      setDeleting(false);
    }
  };

  return (
    // No wrapper card / re-centered container — the detail view sits
    // directly inside .main-card (which is already transparent +
    // edge-to-edge on desktop, capped at 1600px by .has-left-nav
    // .container). Adding our own minHeight + max-width + margin:auto
    // here re-centered the content and made the page feel
    // "containerized" against the rest of the app.
    <div style={{ paddingBottom: 80 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
        <BackButton onClick={onBack} />
        {canDelete && (
          <button
            type="button"
            onClick={deleteCall}
            disabled={deleting}
            style={{
              background: 'none', border: '1px solid var(--room-border, #e2e8f0)',
              color: '#dc2626', borderRadius: 8, padding: '5px 14px',
              fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            {deleting ? 'Deleting…' : 'Delete call'}
          </button>
        )}
      </div>
      {deleteError && (
        <p style={{ color: '#dc2626', fontSize: 13, margin: '0 0 12px' }}>
          Couldn't delete: {deleteError}
        </p>
      )}
      {mode === 'upcoming' ? (
        <UpcomingDetail bot={data} authToken={authToken} onUpdated={load} />
      ) : data.recordingKind === 'meeting' ? (
        <MeetingDetail
          call={data}
          authToken={authToken}
          onUpdated={load}
          authUser={authUser}
          playToken={playToken}
        />
      ) : (
        <CompletedDetail
          call={data}
          authToken={authToken}
          onUpdated={load}
          setPhase={setPhase}
          authUser={authUser}
          playToken={playToken}
        />
      )}
    </div>
  );
}

function BackButton({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: 'none', border: 'none', color: 'var(--room-text-muted)',
        cursor: 'pointer', padding: '4px 0', textAlign: 'left',
        fontSize: 13, marginBottom: 16, fontFamily: 'inherit',
      }}
    >
      ← All calls
    </button>
  );
}

// ─── Detail: upcoming ──────────────────────────────────────────

function UpcomingDetail({ bot, authToken, onUpdated }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [picked, setPicked] = useState(bot.recordingKind);

  const save = async (kind) => {
    setBusy(true);
    setError(null);
    try {
      const r = await fetch(`${API_BASE}/api/qualifying-calls/upcoming/${encodeURIComponent(bot.recallBotId)}/recording-kind`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ recordingKind: kind }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.error || `Save failed (${r.status})`);
      }
      onUpdated();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{
      background: 'var(--room-card)', borderRadius: 14, padding: 28,
      border: '1px solid var(--room-border)',
    }}>
      <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: 'var(--room-text-strong)' }}>
        {bot.meetingTitle || '(untitled meeting)'}
      </h2>
      <p style={{ margin: '6px 0 24px', color: 'var(--room-text-muted)', fontSize: 14 }}>
        Scheduled for {fmtMeetingTime(bot.meetingStartAt)}
      </p>

      <div style={{
        padding: 16, borderRadius: 10,
        background: bot.recordingKindConfirmed ? '#f0fdf4' : '#fef3c7',
        border: `1px solid ${bot.recordingKindConfirmed ? '#bbf7d0' : '#fde68a'}`,
        marginBottom: 24,
      }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: bot.recordingKindConfirmed ? '#166534' : '#92400e' }}>
          {bot.recordingKindConfirmed ? '✓ Type confirmed' : 'Type auto-detected — confirm before the call fires'}
        </div>
        <div style={{ fontSize: 13, color: bot.recordingKindConfirmed ? '#15803d' : '#a16207', marginTop: 4 }}>
          {bot.recordingKindConfirmed
            ? 'You can still flip it any time before the call starts.'
            : 'We picked the most likely type from the meeting title. Pick the right one to silence the bell nag.'}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <KindCard
          kind="qualifying_call"
          picked={picked === 'qualifying_call'}
          confirmedKind={bot.recordingKindConfirmed ? bot.recordingKind : null}
          onPick={() => { setPicked('qualifying_call'); save('qualifying_call'); }}
          busy={busy}
          title="Qualifying Call"
          subtitle="Discovery / intro. Transcribe + qualification-shaped AI summary. No trailer."
        />
        <KindCard
          kind="demo"
          picked={picked === 'demo'}
          confirmedKind={bot.recordingKindConfirmed ? bot.recordingKind : null}
          onPick={() => { setPicked('demo'); save('demo'); }}
          busy={busy}
          title="Demo"
          subtitle="Live product walkthrough. Full pipeline: moment detection, trailer, voice clone narration."
        />
        <KindCard
          kind="meeting"
          picked={picked === 'meeting'}
          confirmedKind={bot.recordingKindConfirmed ? bot.recordingKind : null}
          onPick={() => { setPicked('meeting'); save('meeting'); }}
          busy={busy}
          title="Meeting"
          subtitle="Internal sync, customer check-in, partner call. Transcribe + generic AI summary. No trailer."
        />
      </div>

      {error && (
        <div style={{
          padding: '10px 12px', borderRadius: 8,
          background: 'rgba(239, 68, 68, 0.08)', color: '#dc2626',
          fontSize: 13, marginTop: 12,
        }}>
          {error}
        </div>
      )}
    </div>
  );
}

function KindCard({ kind, picked, confirmedKind, onPick, busy, title, subtitle }) {
  const isConfirmed = confirmedKind === kind;
  return (
    <button
      type="button"
      onClick={onPick}
      disabled={busy}
      style={{
        flex: 1, textAlign: 'left',
        padding: 16, borderRadius: 12,
        border: `2px solid ${picked ? '#0891b2' : 'var(--room-border)'}`,
        background: picked ? 'var(--room-accent-soft)' : 'var(--room-card)',
        cursor: busy ? 'wait' : 'pointer',
        transition: 'all 0.12s ease',
        fontFamily: 'inherit',
        opacity: busy ? 0.7 : 1,
        position: 'relative',
      }}
    >
      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--room-text-strong)', marginBottom: 6 }}>
        {title}
        {isConfirmed && (
          <span style={{ fontSize: 11, color: '#0891b2', marginLeft: 8, fontWeight: 600 }}>
            ✓ Confirmed
          </span>
        )}
      </div>
      <div style={{ fontSize: 13, color: 'var(--room-text-muted)', lineHeight: 1.45 }}>
        {subtitle}
      </div>
    </button>
  );
}

// ─── Detail: completed ────────────────────────────────────────

function CompletedDetail({ call, authToken, onUpdated, setPhase, authUser, playToken }) {
  const summary = call.callSummary || {};
  const isFailed = call.status === 'failed';
  const isProcessing = !isFailed && call.status !== 'completed';
  // Private rows: source-stream requires the signed token — hold the
  // player until it lands rather than mounting a 404ing <video>.
  const canPlaySource = call.sourceAvailable !== false && (!call.isPrivate || !!playToken);
  const sourceSrc = `${API_BASE}/api/source-stream/${encodeURIComponent(call.id)}${playToken ? `?t=${encodeURIComponent(playToken)}` : ''}`;
  const isDisqualified = Boolean(call.disqualifiedAt);
  const isPromoted = Boolean(call.buyerSpaceId);
  const [creating, setCreating] = useState(false);
  const [disqualifying, setDisqualifying] = useState(false);
  const [error, setError] = useState(null);
  const [reasonOpen, setReasonOpen] = useState(false);
  const [reason, setReason] = useState('');
  // Right-rail tab in the 2-column detail layout (2026-05-08).
  // Default 'summary' so the AI verdict + headline are the first
  // thing the seller sees. Topics is a Phase-3 placeholder until
  // the AI pipeline emits chapter segments. Comments + askwinter
  // ship in subsequent commits.
  const [activeTab, setActiveTab] = useState('summary');
  const [reclassifyOpen, setReclassifyOpen] = useState(false);
  // Comment count for the Comments-tab badge. Populated by a
  // single GET on mount + bubbled updates from CommentsThread once
  // it mounts (so post / delete keep the badge in sync).
  const [commentCount, setCommentCount] = useState(0);
  useEffect(() => {
    if (!call?.id || !authToken) return undefined;
    let cancelled = false;
    fetch(`${API_BASE}/api/demos/${encodeURIComponent(call.id)}/comments`, {
      headers: { Authorization: `Bearer ${authToken}` },
    })
      .then((r) => r.ok ? r.json() : { comments: [] })
      .then((d) => {
        if (cancelled) return;
        setCommentCount(Array.isArray(d.comments) ? d.comments.length : 0);
      })
      .catch(() => { /* badge just stays at 0 — non-fatal */ });
    return () => { cancelled = true; };
  }, [call?.id, authToken]);
  // Watch the viewport so the 2-column grid collapses to a single
  // column on narrow screens (<1100px). Avoma-class layouts need
  // ~640px on the left for the video + ~360px on the right for the
  // tab content; below 1100 those don't both fit comfortably.
  const [isNarrowDetail, setIsNarrowDetail] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < 1100
  );
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onResize = () => setIsNarrowDetail(window.innerWidth < 1100);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const videoRef = useRef(null);

  // Seek the video to a specific timestamp + auto-play. Used by the
  // talking-timeline + transcript so any speaker chunk is one click
  // away from the moment in the video. Bails out silently if the
  // video element isn't mounted yet (still-processing state).
  const seekTo = useCallback((seconds) => {
    if (!videoRef.current || typeof seconds !== 'number') return;
    try {
      videoRef.current.currentTime = Math.max(0, seconds);
      const playPromise = videoRef.current.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => { /* user-gesture autoplay block — ignore */ });
      }
      // Smooth-scroll the video into view so the user always sees what
      // they just clicked, even if they were scrolled deep in the
      // transcript.
      videoRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    } catch (_) { /* element detached during navigation */ }
  }, []);

  const transcript = call.transcript || null;
  const segments = Array.isArray(transcript?.segments) ? transcript.segments : [];
  // When the multi-track Recall ingest produced per-participant data,
  // surface the real participant names on the diarized transcript so
  // every place that does labels[speakerId] picks up the proper name.
  // Index participants by their original ordering — multi-track
  // participantId is a Recall platform_user_id (string), but Deepgram's
  // diarization returns numeric speaker IDs for the mixed track. We
  // match top-talker-rank to top-talker-rank so the most-talkative
  // multi-track participant lines up with the most-talkative diarized
  // speaker. Falls through to whatever the transcript already had if
  // no multi-track data exists.
  const participantTalkTime = call.participantTalkTime || null;
  const speakerLabels = useMemo(() => {
    const baseline = (transcript && transcript.speakerLabels) || {};
    if (!participantTalkTime || !Array.isArray(participantTalkTime.participants)) {
      return baseline;
    }
    // Sort speakers in the diarized transcript by total talk-time
    // (descending) and align with the multi-track participants list,
    // which is already sorted by talk-time. Both lists describe the
    // same conversation, just at different attribution layers, so
    // rank-aligned mapping holds.
    const totals = new Map();
    for (const s of segments) {
      if (s.speaker == null) continue;
      const dur = Math.max(0, (s.end || 0) - (s.start || 0));
      totals.set(s.speaker, (totals.get(s.speaker) || 0) + dur);
    }
    const rankedSpeakers = [...totals.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([id]) => id);
    const merged = { ...baseline };
    rankedSpeakers.forEach((speakerId, idx) => {
      const p = participantTalkTime.participants[idx];
      if (p && p.name) merged[speakerId] = p.name;
    });
    return merged;
  }, [participantTalkTime, transcript, segments]);

  // Stat strip + talking-timeline both want the same per-speaker
  // totals. When multi-track data is present, prefer it directly
  // (more accurate breakdowns); otherwise compute from diarization.
  const baseSpeakerStats = useMemo(() => {
    if (participantTalkTime && Array.isArray(participantTalkTime.participants) && participantTalkTime.participants.length) {
      return {
        totalSeconds: participantTalkTime.totalSpeakerSeconds || 0,
        bySpeaker: participantTalkTime.participants.map((p, idx) => ({
          speaker: p.participantId || `mt_${idx}`,
          seconds: p.totalSeconds || 0,
          pct: p.pct || 0,
          label: p.name || `Participant ${idx + 1}`,
          sublabel: p.email || (p.isHost ? 'Host' : null),
          color: getSpeakerColor(idx, idx),
          segments: Array.isArray(p.segments) ? p.segments : [],
        })),
      };
    }
    return computeSpeakerStats(segments, speakerLabels);
  }, [participantTalkTime, segments, speakerLabels]);

  // Layer the resolver sources on top of baseSpeakerStats. The
  // priority order (override > multi-track > calendar > NER > generic)
  // is enforced inside applySpeakerNameLayers. Memo deps include every
  // data source so a fresh API response re-flows naturally.
  const speakerStats = useMemo(() => ({
    ...baseSpeakerStats,
    bySpeaker: applySpeakerNameLayers(baseSpeakerStats.bySpeaker, {
      speakerNameOverrides: call.speakerNameOverrides,
      calendarAttendees: call.calendarAttendees,
      extractedNames: call.extractedNames,
      demoAttendees: call.attendees,
    }),
  }), [baseSpeakerStats, call.speakerNameOverrides, call.calendarAttendees, call.extractedNames, call.attendees]);

  // Rename handler — POST to the backend then trigger a parent refetch.
  // Throws on failure so the inline rename UI in CallReviewSections can
  // re-open the editor + show an error toast. Optimistic UI would be
  // nicer but the round-trip is fast (sub-200ms typically) and the
  // refetch picks up any server-side normalization.
  const handleSpeakerRename = useCallback(async (speakerId, newName) => {
    const r = await fetch(
      `${API_BASE}/api/demos/${encodeURIComponent(call.id)}/speakers/${encodeURIComponent(String(speakerId))}/name`,
      {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name: newName }),
      }
    );
    if (!r.ok) {
      const body = await r.json().catch(() => ({}));
      throw new Error(body.error || `Server returned ${r.status}`);
    }
    if (onUpdated) onUpdated();
  }, [call.id, authToken, onUpdated]);

  const createSpace = async () => {
    if (!window.confirm('Create a Decision Room from this call? Discovery copy will be auto-drafted from the transcript and you\'ll be dropped into the Space to review.')) return;
    setCreating(true);
    setError(null);
    try {
      const r = await fetch(`${API_BASE}/api/qualifying-calls/${encodeURIComponent(call.id)}/create-space`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.error || `Create failed (${r.status})`);
      }
      const result = await r.json();
      // Deep-link into the new Space at the Discovery section so the
      // AE can review/edit the AI draft before sending.
      const targetHash = `#/buyer-spaces/${result.spaceId}/${result.section || 'discovery'}`;
      if (typeof window !== 'undefined') {
        window.history.pushState(null, '', window.location.pathname + window.location.search + targetHash);
      }
      setPhase('buyer-spaces');
    } catch (err) {
      setError(err.message);
      setCreating(false);
    }
  };

  const disqualify = async () => {
    setDisqualifying(true);
    setError(null);
    try {
      const r = await fetch(`${API_BASE}/api/qualifying-calls/${encodeURIComponent(call.id)}/disqualify`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: reason.trim() || null }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.error || `Disqualify failed (${r.status})`);
      }
      setReasonOpen(false);
      onUpdated();
    } catch (err) {
      setError(err.message);
    } finally {
      setDisqualifying(false);
    }
  };

  const requalify = async () => {
    setError(null);
    try {
      const r = await fetch(`${API_BASE}/api/qualifying-calls/${encodeURIComponent(call.id)}/requalify`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.error || `Requalify failed (${r.status})`);
      }
      onUpdated();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Header */}
      <div style={{
        // Header card — uses the solid card surface so it doesn't
        // bleed white onto dark mode. The accent-tinted bottom of
        // the original gradient is dropped; the page-level accent
        // wash above already provides that color cue.
        background: 'var(--room-card)',
        borderRadius: 16, padding: 28,
        border: '1px solid var(--room-border)',
        boxShadow: '0 4px 16px rgba(8, 145, 178, 0.06)',
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 260 }}>
            <h2 style={{
              margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--room-text-strong)',
              letterSpacing: '-0.01em',
            }}>
              {summary.company || call.prospectName || call.filename || '(untitled call)'}
            </h2>
            <p style={{ margin: '6px 0 0', color: 'var(--room-text-muted)', fontSize: 14 }}>
              Recorded {fmtDate(call.completedAt || call.createdAt)}
              {' · '}
              <button
                type="button"
                onClick={() => setReclassifyOpen(true)}
                style={{
                  background: 'none', border: 'none', padding: 0,
                  color: '#0891b2', fontSize: 13, fontWeight: 600,
                  cursor: 'pointer', fontFamily: 'inherit',
                  textDecoration: 'underline', textUnderlineOffset: 2,
                }}
              >
                Re-tag this call
              </button>
            </p>
          </div>
          {speakerStats.totalSeconds > 0 && (
            <div style={{
              display: 'flex', gap: 18,
              padding: '10px 16px', background: 'var(--room-card)',
              border: '1px solid var(--room-border)', borderRadius: 10,
            }}>
              <Stat label="Length" value={fmtDuration(call.durationSeconds || speakerStats.totalSeconds)} />
              <Divider />
              <Stat label="Speakers" value={speakerStats.bySpeaker.length} />
              {speakerStats.bySpeaker[0] && (
                <>
                  <Divider />
                  <Stat
                    label="Top talker"
                    value={`${speakerStats.bySpeaker[0].label} · ${speakerStats.bySpeaker[0].pct}%`}
                  />
                </>
              )}
            </div>
          )}
        </div>

      </div>

      {/* ─── 2-column detail (2026-05-08, Avoma-style) ──────────────
          Left: large video player + talking-timeline below.
          Right: tabbed panel — Summary / Topics / Transcript /
                 Comments / Ask Winter.
          Collapses to single column under 1100px viewport.
          Topics / Comments / Ask Winter render placeholder content
          until subsequent commits wire them up.
      ─────────────────────────────────────────────────────────────── */}
      <div style={{
        display: 'grid',
        // Right rail width: now that the detail flows through the
        // app's 1600px container (no more 900px nested wrapper), we
        // can afford a substantial right rail without starving the
        // video. 420-520 keeps the Summary / Topics / Transcript
        // panel readable (40-60ch) on 1440px+ monitors and shrinks
        // gracefully on narrower laptops.
        gridTemplateColumns: isNarrowDetail ? 'minmax(0, 1fr)' : 'minmax(0, 1fr) minmax(420px, 520px)',
        gap: 20,
      }}>
        {/* LEFT — video + talking timeline */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>
          {/* Recording playback. The qualifying-call pipeline persists
              the source MP4 to R2 (no compose step, no trailer),
              so /api/source-stream/<id> is the only video endpoint
              available. preload="metadata" so the browser shows the
              first frame as the poster without downloading the whole
              file until the AE actually clicks play. */}
          {!isProcessing && canPlaySource && (
            <video
              ref={videoRef}
              controls
              preload="metadata"
              playsInline
              src={sourceSrc}
              style={{
                width: '100%',
                aspectRatio: '16 / 9',
                borderRadius: 12, background: 'var(--room-text-strong, #0f172a)',
                display: 'block',
                boxShadow: '0 6px 20px rgba(15, 23, 42, 0.18)',
              }}
            >
              Your browser doesn't support HTML5 video.
            </video>
          )}
          {!isProcessing && call.sourceAvailable === false && (
            <div style={{
              width: '100%', aspectRatio: '16 / 9', borderRadius: 12,
              background: 'var(--room-text-strong, #0f172a)', color: 'var(--room-text-subtle, #94a3b8)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 14, textAlign: 'center', padding: 24,
            }}>
              No recording file is available for this call — the transcript and AI summary below are still here.
            </div>
          )}

          {/* Color-coded topic chapter strip directly under the
              player. Each colored block = one extracted topic from
              callSummary.topics; click to seek. Self-renders null
              if topics or duration are missing, so we mount it
              unconditionally. */}
          {!isProcessing && (
            <TopicChapterStrip
              segments={summary.topics}
              totalDurationSeconds={call.durationSeconds || speakerStats?.totalSeconds}
              onSeek={seekTo}
              videoRef={videoRef /* unlocks active-segment highlight + [/] keyboard nav */}
            />
          )}

          {/* Talking timeline directly under the video — clickable
              speaker bars seek the video to the picked moment. Sits
              full-width on the left column so it spans the full
              video timeline without competing with the right rail. */}
          {!isProcessing && speakerStats && speakerStats.totalSeconds > 0 && (
            <div style={{
              background: 'var(--room-card)', borderRadius: 12,
              border: '1px solid var(--room-border)',
              padding: 16,
            }}>
              <div style={{
                fontSize: 12, fontWeight: 700, letterSpacing: '0.06em',
                textTransform: 'uppercase', color: 'var(--room-text-subtle)',
                marginBottom: 10,
              }}>
                Talk time
              </div>
              <TalkingTimeline
                segments={segments}
                speakerLabels={speakerLabels}
                speakerStats={speakerStats}
                onSeek={seekTo}
                onRename={handleSpeakerRename}
              />
            </div>
          )}
        </div>

        {/* RIGHT — tabbed panel */}
        <div style={{
          background: 'var(--room-card)', borderRadius: 12,
          border: '1px solid var(--room-border)',
          display: 'flex', flexDirection: 'column',
          minHeight: 480,
          overflow: 'hidden',
        }}>
          {/* Tab strip */}
          <div style={{
            display: 'flex', gap: 0,
            borderBottom: '1px solid var(--room-border)',
            background: 'rgba(248, 250, 252, 0.5)',
            overflowX: 'auto',
          }}>
            {[
              { id: 'summary',    label: 'Summary' },
              { id: 'topics',     label: 'Topics' },
              { id: 'transcript', label: 'Transcript' },
              { id: 'comments',   label: 'Comments' },
              { id: 'askwinter',  label: 'Ask Winter' },
            ].map((t) => {
              const active = activeTab === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setActiveTab(t.id)}
                  style={{
                    appearance: 'none',
                    border: 'none',
                    background: 'transparent',
                    padding: '12px 14px',
                    fontFamily: 'inherit',
                    fontSize: 13,
                    fontWeight: active ? 700 : 500,
                    color: active ? '#0891b2' : 'var(--room-text-muted)',
                    borderBottom: `2px solid ${active ? '#0891b2' : 'transparent'}`,
                    cursor: 'pointer',
                    transition: 'color 120ms ease, border-color 120ms ease',
                    flexShrink: 0,
                  }}
                >
                  {t.label}
                  {/* Count badge — shown on the Comments tab when
                      there are any comments. Helps the seller spot
                      teammate activity without opening the tab. */}
                  {t.id === 'comments' && commentCount > 0 && (
                    <span style={{
                      marginLeft: 6,
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      minWidth: 18, height: 18, padding: '0 5px',
                      background: active ? '#0891b2' : 'var(--room-border, #cbd5e1)',
                      color: 'white', borderRadius: 10,
                      fontSize: 11, fontWeight: 700,
                      fontVariantNumeric: 'tabular-nums',
                    }}>
                      {commentCount}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Tab content. Each tab body has its own scroll context so
              long transcripts / comment threads don't blow out the
              page layout. Min-height ensures the right rail doesn't
              collapse to nothing on the placeholder tabs. */}
          <div style={{
            flex: 1, padding: 16,
            overflowY: 'auto',
            maxHeight: 600,
          }}>
            {activeTab === 'summary' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {summary.headline && (
                  <p style={{
                    margin: 0, fontSize: 14, lineHeight: 1.55,
                    color: 'var(--room-text-body)',
                  }}>
                    {summary.headline}
                  </p>
                )}
                {summary.qualification_verdict && (
                  <div style={{
                    padding: '12px 14px',
                    background: '#f0f9ff', borderRadius: 10,
                    border: '1px solid #bae6fd',
                    fontSize: 13, color: '#0c4a6e',
                    lineHeight: 1.5,
                  }}>
                    <strong style={{ textTransform: 'capitalize' }}>
                      AI verdict: {summary.qualification_verdict}
                    </strong>
                    {summary.verdict_reasoning && (
                      <div style={{ marginTop: 4, color: '#075985' }}>
                        {summary.verdict_reasoning}
                      </div>
                    )}
                  </div>
                )}
                <SummaryCard summary={summary} />
                {!summary.headline && !summary.qualification_verdict && !summary.key_points && (
                  <p style={{ color: 'var(--room-text-muted)', fontSize: 13, margin: 0 }}>
                    AI summary still processing. Refresh in a minute.
                  </p>
                )}
              </div>
            )}

            {activeTab === 'topics' && (
              <TopicsList topics={summary.topics} onSeek={seekTo} />
            )}

            {/* Transcript gets the RESOLVED labels merged in (override →
                multi-track → calendar → NER → email signature) — the raw
                transcript says "Speaker 0/1" while the talk-time panel
                next to it shows real names. Same shape MeetingDetail
                passes. */}
            {activeTab === 'transcript' && (
              <TranscriptPanel transcript={{ ...transcript, speakerLabels }} onSeek={seekTo} />
            )}

            {activeTab === 'comments' && (
              <CommentsThread
                demoId={call.id}
                authToken={authToken}
                currentUser={authUser}
                onCountChange={setCommentCount}
              />
            )}

            {activeTab === 'askwinter' && (
              <AskWinterCallTab callId={call.id} authToken={authToken} />
            )}
          </div>
        </div>
      </div>

      {/* Status banner for promoted/disqualified */}
      {isPromoted && (
        <div style={{
          padding: 16, borderRadius: 12,
          background: 'var(--room-accent-soft, #faf5ff)', border: '1px solid #e9d5ff',
        }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#6d28d9' }}>
            ✓ Decision Room already created from this call
          </div>
          <button
            type="button"
            onClick={() => {
              const targetHash = `#/buyer-spaces/${call.buyerSpaceId}/discovery`;
              if (typeof window !== 'undefined') {
                window.history.pushState(null, '', window.location.pathname + window.location.search + targetHash);
              }
              setPhase('buyer-spaces');
            }}
            style={{
              marginTop: 8, padding: '8px 14px', borderRadius: 8,
              border: '1px solid #c4b5fd', background: 'var(--room-card)',
              color: '#6d28d9', cursor: 'pointer',
              fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
            }}
          >
            Open the Decision Room →
          </button>
        </div>
      )}

      {isDisqualified && !isPromoted && (
        <div style={{
          padding: 16, borderRadius: 12,
          background: 'var(--room-surface)', border: '1px solid var(--room-border)',
        }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--room-text-body)' }}>
            Marked not qualified
          </div>
          {call.disqualifiedReason && (
            <div style={{ fontSize: 13, color: 'var(--room-text-muted)', marginTop: 4 }}>
              Reason: {call.disqualifiedReason}
            </div>
          )}
          <button
            type="button"
            onClick={requalify}
            style={{
              marginTop: 10, padding: '8px 14px', borderRadius: 8,
              border: '1px solid var(--room-border)', background: 'var(--room-card)',
              color: 'var(--room-text-body)', cursor: 'pointer',
              fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
            }}
          >
            Undo (move back to awaiting review)
          </button>
        </div>
      )}

      {/* Old PillSwitcher block removed 2026-05-08 — replaced by the
          2-column layout above. Talking timeline now lives under the
          video on the left; AI Summary and Transcript live in the
          right rail's tab content. */}

      {isFailed && (
        <div style={{
          padding: 16, borderRadius: 12,
          background: '#fef2f2', border: '1px solid #fecaca',
          color: '#991b1b', fontSize: 14,
        }}>
          Processing failed for this recording — the AI summary couldn't be generated.
          Re-uploading the recording usually fixes it; contact support if it keeps happening.
        </div>
      )}
      {isProcessing && (
        <div style={{
          padding: 16, borderRadius: 12,
          background: '#ecfeff', border: '1px solid #bae6fd',
          color: '#0c4a6e', fontSize: 14,
        }}>
          Recording is still processing ({call.progress || 0}%). Refresh in a minute to see the AI summary.
        </div>
      )}

      {/* Action row */}
      {!isProcessing && !isFailed && !isPromoted && !isDisqualified && (
        <div style={{
          background: 'var(--room-card)', borderRadius: 14, padding: 20,
          border: '1px solid var(--room-border)',
          display: 'flex', flexDirection: 'column', gap: 12,
        }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--room-text-strong)' }}>
            Decision
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={createSpace}
              disabled={creating}
              style={{
                padding: '12px 20px', borderRadius: 10,
                border: 'none', cursor: creating ? 'wait' : 'pointer',
                background: 'linear-gradient(135deg, #0891b2 0%, #0ea5e9 50%, #6366f1 100%)',
                color: '#fff', fontSize: 14, fontWeight: 600, fontFamily: 'inherit',
                boxShadow: '0 4px 12px rgba(8, 145, 178, 0.25)',
                opacity: creating ? 0.7 : 1,
              }}
            >
              {creating ? 'Creating Decision Room…' : '✓ Create Decision Room'}
            </button>
            {!reasonOpen ? (
              <button
                type="button"
                onClick={() => setReasonOpen(true)}
                style={{
                  padding: '12px 20px', borderRadius: 10,
                  border: '1px solid var(--room-border)', background: 'var(--room-card)',
                  color: 'var(--room-text-body)', cursor: 'pointer',
                  fontSize: 14, fontWeight: 500, fontFamily: 'inherit',
                }}
              >
                Mark not qualified
              </button>
            ) : null}
          </div>
          {reasonOpen && (
            <div style={{ marginTop: 4, padding: 14, background: 'var(--room-surface)', borderRadius: 10, border: '1px solid var(--room-border)' }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--room-text-muted)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Reason (optional, helps your funnel analytics later)
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                placeholder="e.g. budget too small / wrong stakeholder / no current pain"
                style={{
                  width: '100%', padding: '8px 10px',
                  border: '1px solid var(--room-border)', borderRadius: 8,
                  fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box',
                  resize: 'vertical',
                }}
              />
              <div style={{ display: 'flex', gap: 8, marginTop: 10, justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => { setReasonOpen(false); setReason(''); }}
                  disabled={disqualifying}
                  style={{
                    padding: '8px 14px', borderRadius: 8,
                    border: '1px solid var(--room-border)', background: 'var(--room-card)',
                    color: 'var(--room-text-body)', cursor: 'pointer',
                    fontSize: 13, fontWeight: 500, fontFamily: 'inherit',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={disqualify}
                  disabled={disqualifying}
                  style={{
                    padding: '8px 14px', borderRadius: 8,
                    border: 'none', background: '#dc2626',
                    color: '#fff', cursor: disqualifying ? 'wait' : 'pointer',
                    fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
                    opacity: disqualifying ? 0.7 : 1,
                  }}
                >
                  {disqualifying ? 'Saving…' : 'Confirm not qualified'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {error && (
        <div style={{
          padding: '10px 12px', borderRadius: 8,
          background: 'rgba(239, 68, 68, 0.08)', color: '#dc2626',
          fontSize: 13,
        }}>
          {error}
        </div>
      )}

      <ReclassifyModal
        open={reclassifyOpen}
        recordingId={call.id}
        currentKind="qualifying_call"
        authToken={authToken}
        onClose={() => setReclassifyOpen(false)}
        onReclassified={() => onUpdated()}
      />
    </div>
  );
}

// ─── Detail: meeting (generic AI summary, no qualify/disqualify) ────
//
// Mirrors CompletedDetail's layout (header + 3-pill review + reclassify)
// but skips the "Create Decision Room" / "Mark not qualified" actions
// (those are qualifying-call-specific) and renders MeetingSummaryCard
// for the AI Summary pill.
function MeetingDetail({ call, authToken, onUpdated, authUser, playToken }) {
  const summary = call.callSummary || {};
  const isFailed = call.status === 'failed';
  const isProcessing = !isFailed && call.status !== 'completed';
  // Same private-source gating as CompletedDetail.
  const canPlaySource = call.sourceAvailable !== false && (!call.isPrivate || !!playToken);
  const sourceSrc = `${API_BASE}/api/source-stream/${encodeURIComponent(call.id)}${playToken ? `?t=${encodeURIComponent(playToken)}` : ''}`;
  // Same 2-column layout as CompletedDetail (2026-05-08).
  const [activeTab, setActiveTab] = useState('summary');
  // Create a Decision Room from this meeting (2026-08-28, flag:
  // rooms_any_recording). Same endpoint the qualifying-call flow uses;
  // the backend maps the meeting summary to Discovery and seeds the
  // MAP from its action items.
  const [creatingRoom, setCreatingRoom] = useState(false);
  const [createRoomError, setCreateRoomError] = useState(null);
  const canCreateRoom = !!authUser?.company?.featureFlags?.rooms_any_recording
    && call.status === 'completed';
  const createRoomFromMeeting = async () => {
    if (!window.confirm('Create a Decision Room from this meeting?\n\nThe meeting summary becomes the Discovery draft, and its action items seed the Mutual Action Plan. You review everything before sharing.')) return;
    setCreatingRoom(true);
    setCreateRoomError(null);
    try {
      const r = await fetch(`${API_BASE}/api/qualifying-calls/${encodeURIComponent(call.id)}/create-space`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body.error || `Create failed (${r.status})`);
      const targetHash = `#/buyer-spaces/${body.spaceId}/${body.section || 'discovery'}`;
      window.history.pushState(null, '', window.location.pathname + window.location.search + targetHash);
      window.dispatchEvent(new Event('hashchange'));
    } catch (err) {
      setCreateRoomError(err.message);
      setCreatingRoom(false);
    }
  };
  const [reclassifyOpen, setReclassifyOpen] = useState(false);
  // Comment count for the Comments-tab badge — see CompletedDetail
  // for rationale.
  const [commentCount, setCommentCount] = useState(0);
  useEffect(() => {
    if (!call?.id || !authToken) return undefined;
    let cancelled = false;
    fetch(`${API_BASE}/api/demos/${encodeURIComponent(call.id)}/comments`, {
      headers: { Authorization: `Bearer ${authToken}` },
    })
      .then((r) => r.ok ? r.json() : { comments: [] })
      .then((d) => {
        if (cancelled) return;
        setCommentCount(Array.isArray(d.comments) ? d.comments.length : 0);
      })
      .catch(() => { /* badge just stays at 0 — non-fatal */ });
    return () => { cancelled = true; };
  }, [call?.id, authToken]);
  const [isNarrowDetail, setIsNarrowDetail] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < 1100
  );
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onResize = () => setIsNarrowDetail(window.innerWidth < 1100);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const videoRef = useRef(null);

  const seekTo = useCallback((seconds) => {
    if (!videoRef.current || typeof seconds !== 'number') return;
    try {
      videoRef.current.currentTime = Math.max(0, seconds);
      const playPromise = videoRef.current.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => { /* autoplay blocked — ignore */ });
      }
      videoRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    } catch (_) { /* element detached */ }
  }, []);

  const transcript = call.transcript || null;
  const segments = Array.isArray(transcript?.segments) ? transcript.segments : [];
  const participantTalkTime = call.participantTalkTime || null;
  const speakerLabels = useMemo(() => {
    const baseline = (transcript && transcript.speakerLabels) || {};
    if (!participantTalkTime || !Array.isArray(participantTalkTime.participants)) return baseline;
    const totals = new Map();
    for (const s of segments) {
      if (s.speaker == null) continue;
      const dur = Math.max(0, (s.end || 0) - (s.start || 0));
      totals.set(s.speaker, (totals.get(s.speaker) || 0) + dur);
    }
    const ranked = [...totals.entries()].sort((a, b) => b[1] - a[1]).map(([id]) => id);
    const merged = { ...baseline };
    ranked.forEach((spId, idx) => {
      const p = participantTalkTime.participants[idx];
      if (p && p.name) merged[spId] = p.name;
    });
    return merged;
  }, [participantTalkTime, transcript, segments]);

  const baseSpeakerStats = useMemo(() => {
    if (participantTalkTime && Array.isArray(participantTalkTime.participants) && participantTalkTime.participants.length) {
      return {
        totalSeconds: participantTalkTime.totalSpeakerSeconds || 0,
        bySpeaker: participantTalkTime.participants.map((p, idx) => ({
          speaker: p.participantId || `mt_${idx}`,
          seconds: p.totalSeconds || 0,
          pct: p.pct || 0,
          label: p.name || `Participant ${idx + 1}`,
          sublabel: p.email || (p.isHost ? 'Host' : null),
          color: getSpeakerColor(idx, idx),
          segments: Array.isArray(p.segments) ? p.segments : [],
        })),
      };
    }
    return computeSpeakerStats(segments, speakerLabels);
  }, [participantTalkTime, segments, speakerLabels]);

  // Same layered resolver as CompletedDetail — applies user overrides,
  // calendar attendees, and NER results on top of whichever base
  // (multi-track or diarized) was used above. Keeps the priority logic
  // in one place (applySpeakerNameLayers in CallReviewSections.jsx).
  const speakerStats = useMemo(() => ({
    ...baseSpeakerStats,
    bySpeaker: applySpeakerNameLayers(baseSpeakerStats.bySpeaker, {
      speakerNameOverrides: call.speakerNameOverrides,
      calendarAttendees: call.calendarAttendees,
      extractedNames: call.extractedNames,
      demoAttendees: call.attendees,
    }),
  }), [baseSpeakerStats, call.speakerNameOverrides, call.calendarAttendees, call.extractedNames, call.attendees]);

  // Inline rename handler for the talking-timeline's pencil icon.
  // See the matching handler in CompletedDetail for the rationale; the
  // two functions are structurally identical because the talk-time
  // view itself is shared between both detail surfaces.
  const handleSpeakerRename = useCallback(async (speakerId, newName) => {
    const r = await fetch(
      `${API_BASE}/api/demos/${encodeURIComponent(call.id)}/speakers/${encodeURIComponent(String(speakerId))}/name`,
      {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name: newName }),
      }
    );
    if (!r.ok) {
      const body = await r.json().catch(() => ({}));
      throw new Error(body.error || `Server returned ${r.status}`);
    }
    if (onUpdated) onUpdated();
  }, [call.id, authToken, onUpdated]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{
        background: 'var(--room-card)',
        borderRadius: 16, padding: 28,
        border: '1px solid var(--room-border)',
        boxShadow: '0 4px 16px rgba(99, 102, 241, 0.06)',
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 260 }}>
            <h2 style={{
              margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--room-text-strong)',
              letterSpacing: '-0.01em',
            }}>
              {summary.company || call.prospectName || call.filename || '(untitled meeting)'}
            </h2>
            <p style={{ margin: '6px 0 0', color: 'var(--room-text-muted)', fontSize: 14 }}>
              Recorded {fmtDate(call.completedAt || call.createdAt)}
              {' · '}
              <button
                type="button"
                onClick={() => setReclassifyOpen(true)}
                style={{
                  background: 'none', border: 'none', padding: 0,
                  color: '#6366f1', fontSize: 13, fontWeight: 600,
                  cursor: 'pointer', fontFamily: 'inherit',
                  textDecoration: 'underline', textUnderlineOffset: 2,
                }}
              >
                Re-tag this call
              </button>
              {canCreateRoom && (
                <>
                  {' · '}
                  <button
                    type="button"
                    onClick={createRoomFromMeeting}
                    disabled={creatingRoom}
                    style={{
                      background: 'none', border: 'none', padding: 0,
                      color: '#6366f1', fontSize: 13, fontWeight: 600,
                      cursor: 'pointer', fontFamily: 'inherit',
                      textDecoration: 'underline', textUnderlineOffset: 2,
                    }}
                  >
                    {creatingRoom ? 'Creating room…' : 'Create Decision Room'}
                  </button>
                </>
              )}
              {createRoomError && (
                <span style={{ color: '#dc2626', fontSize: 12, marginLeft: 8 }}>{createRoomError}</span>
              )}
            </p>
          </div>
          {speakerStats.totalSeconds > 0 && (
            <div style={{
              display: 'flex', gap: 18, padding: '10px 16px',
              background: 'var(--room-card)', border: '1px solid var(--room-border)', borderRadius: 10,
            }}>
              <Stat label="Length" value={fmtDuration(call.durationSeconds || speakerStats.totalSeconds)} />
              <Divider />
              <Stat label="Speakers" value={speakerStats.bySpeaker.length} />
              {speakerStats.bySpeaker[0] && (
                <>
                  <Divider />
                  <Stat
                    label="Top talker"
                    value={`${speakerStats.bySpeaker[0].label} · ${speakerStats.bySpeaker[0].pct}%`}
                  />
                </>
              )}
            </div>
          )}
        </div>

      </div>

      {/* 2-column detail layout — same shape as CompletedDetail. */}
      {!isProcessing && !isFailed ? (
        <div style={{
          display: 'grid',
          // Mirrors CompletedDetail above — see comment there.
          gridTemplateColumns: isNarrowDetail ? 'minmax(0, 1fr)' : 'minmax(0, 1fr) minmax(420px, 520px)',
          gap: 20,
        }}>
          {/* LEFT — video + talking timeline */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>
            {canPlaySource ? (
              <video
                ref={videoRef}
                controls
                preload="metadata"
                playsInline
                src={sourceSrc}
                style={{
                  width: '100%',
                  aspectRatio: '16 / 9',
                  borderRadius: 12, background: 'var(--room-text-strong, #0f172a)', display: 'block',
                  boxShadow: '0 6px 20px rgba(15, 23, 42, 0.18)',
                }}
              >
                Your browser doesn't support HTML5 video.
              </video>
            ) : call.sourceAvailable === false ? (
              <div style={{
                width: '100%', aspectRatio: '16 / 9', borderRadius: 12,
                background: 'var(--room-text-strong, #0f172a)', color: 'var(--room-text-subtle, #94a3b8)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 14, textAlign: 'center', padding: 24,
              }}>
                No recording file is available for this meeting — the transcript and AI summary are still here.
              </div>
            ) : null}

            {/* Color-coded topic chapter strip — see CompletedDetail
                for rationale. Same component, same data shape. Duration
                from the transcript's real end timestamp (backend
                durationSeconds), falling back to summed talk time —
                summed segments understate wall-clock (silence/overlap)
                and mis-scaled chapter positions. */}
            <TopicChapterStrip
              segments={summary.topics}
              totalDurationSeconds={call.durationSeconds || speakerStats?.totalSeconds}
              onSeek={seekTo}
              videoRef={videoRef /* unlocks active-segment highlight + [/] keyboard nav */}
            />

            {speakerStats && speakerStats.totalSeconds > 0 && (
              <div style={{
                background: 'var(--room-card)', borderRadius: 12,
                border: '1px solid var(--room-border)',
                padding: 16,
              }}>
                <div style={{
                  fontSize: 12, fontWeight: 700, letterSpacing: '0.06em',
                  textTransform: 'uppercase', color: 'var(--room-text-subtle)',
                  marginBottom: 10,
                }}>
                  Talk time
                </div>
                <TalkingTimeline
                  segments={segments}
                  speakerLabels={speakerLabels}
                  speakerStats={speakerStats}
                  onSeek={seekTo}
                  onRename={handleSpeakerRename}
                />
              </div>
            )}
          </div>

          {/* RIGHT — tabbed panel */}
          <div style={{
            background: 'var(--room-card)', borderRadius: 12,
            border: '1px solid var(--room-border)',
            display: 'flex', flexDirection: 'column',
            minHeight: 480,
            overflow: 'hidden',
          }}>
            <div style={{
              display: 'flex', gap: 0,
              borderBottom: '1px solid var(--room-border)',
              background: 'rgba(248, 250, 252, 0.5)',
              overflowX: 'auto',
            }}>
              {[
                { id: 'summary',    label: 'Summary' },
                { id: 'topics',     label: 'Topics' },
                { id: 'transcript', label: 'Transcript' },
                { id: 'comments',   label: 'Comments' },
                { id: 'askwinter',  label: 'Ask Winter' },
              ].map((t) => {
                const active = activeTab === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setActiveTab(t.id)}
                    style={{
                      appearance: 'none', border: 'none', background: 'transparent',
                      padding: '12px 14px',
                      fontFamily: 'inherit', fontSize: 13,
                      fontWeight: active ? 700 : 500,
                      color: active ? '#6366f1' : 'var(--room-text-muted)',
                      borderBottom: `2px solid ${active ? '#6366f1' : 'transparent'}`,
                      cursor: 'pointer',
                      transition: 'color 120ms ease, border-color 120ms ease',
                      flexShrink: 0,
                    }}
                  >
                    {t.label}
                    {/* Count badge — mirrors CompletedDetail. The count
                        state was already fetched; it just never rendered
                        here. */}
                    {t.id === 'comments' && commentCount > 0 && (
                      <span style={{
                        marginLeft: 6,
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        minWidth: 18, height: 18, padding: '0 5px',
                        background: active ? '#6366f1' : 'var(--room-border, #cbd5e1)',
                        color: 'white', borderRadius: 10,
                        fontSize: 11, fontWeight: 700,
                        fontVariantNumeric: 'tabular-nums',
                      }}>
                        {commentCount}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            <div style={{ flex: 1, padding: 16, overflowY: 'auto', maxHeight: 600 }}>
              {activeTab === 'summary' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {summary.headline && (
                    <p style={{ margin: 0, fontSize: 14, lineHeight: 1.55, color: 'var(--room-text-body)' }}>
                      {summary.headline}
                    </p>
                  )}
                  <MeetingSummaryCard summary={summary} />
                </div>
              )}
              {activeTab === 'topics' && (
                <TopicsList topics={summary.topics} onSeek={seekTo} />
              )}
              {activeTab === 'transcript' && (
                <TranscriptPanel transcript={{ ...transcript, speakerLabels }} onSeek={seekTo} />
              )}
              {activeTab === 'comments' && (
                <CommentsThread
                  demoId={call.id}
                  authToken={authToken}
                  currentUser={authUser}
                  onCountChange={setCommentCount}
                />
              )}
              {activeTab === 'askwinter' && (
                <AskWinterCallTab callId={call.id} authToken={authToken} />
              )}
            </div>
          </div>
        </div>
      ) : isFailed ? (
        <div style={{
          padding: 16, borderRadius: 12,
          background: '#fef2f2', border: '1px solid #fecaca',
          color: '#991b1b', fontSize: 14,
        }}>
          Processing failed for this recording — the AI summary couldn't be generated.
          Re-uploading the recording usually fixes it; contact support if it keeps happening.
        </div>
      ) : (
        <div style={{
          padding: 16, borderRadius: 12,
          background: '#ecfeff', border: '1px solid #bae6fd',
          color: '#0c4a6e', fontSize: 14,
        }}>
          Recording is still processing ({call.progress || 0}%). Refresh in a minute to see the AI summary.
        </div>
      )}

      <ReclassifyModal
        open={reclassifyOpen}
        recordingId={call.id}
        currentKind="meeting"
        authToken={authToken}
        onClose={() => setReclassifyOpen(false)}
        onReclassified={() => onUpdated()}
      />
    </div>
  );
}

// AI summary card for meetings — shape: { key_takeaways, decisions_made,
// action_items, open_questions, context_for_next_call, participants }.
// Distinct from SummaryCard which is qualifying-call-shaped.
function MeetingSummaryCard({ summary }) {
  const sections = [
    { label: 'Key takeaways',  items: summary.key_takeaways },
    { label: 'Decisions made', items: summary.decisions_made },
    { label: 'Action items',   items: summary.action_items },
    { label: 'Open questions', items: summary.open_questions },
    { label: 'Participants',   items: summary.participants },
  ].filter((s) => Array.isArray(s.items) && s.items.length > 0);

  const singletons = [
    { label: 'Context for next call', value: summary.context_for_next_call },
  ].filter((s) => s.value);

  if (sections.length === 0 && singletons.length === 0) {
    return (
      <div style={{
        background: 'var(--room-card)', borderRadius: 14, padding: 28,
        border: '1px solid var(--room-border)', textAlign: 'center', color: 'var(--room-text-muted)', fontSize: 14,
      }}>
        AI summary is still generating, or no notable signal was detected. Refresh in a minute.
      </div>
    );
  }

  return (
    <div style={{
      background: 'var(--room-card)', borderRadius: 14, padding: 24,
      border: '1px solid var(--room-border)',
    }}>
      <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 700, color: 'var(--room-text-strong)' }}>
        AI Summary
      </h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
        {sections.map((s) => (
          <div key={s.label}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--room-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
              {s.label}
            </div>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 14, color: 'var(--room-text-body)', lineHeight: 1.5 }}>
              {s.items.map((item, i) => (<li key={i}>{item}</li>))}
            </ul>
          </div>
        ))}
        {singletons.map((s) => (
          <div key={s.label}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--room-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
              {s.label}
            </div>
            <div style={{ fontSize: 14, color: 'var(--room-text-body)', lineHeight: 1.5 }}>
              {s.value}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// PillSwitcher / Stat / Divider / TalkingTimeline / TranscriptPanel +
// shared helpers (getSpeakerColor, fmtDuration, computeSpeakerStats)
// moved to ./CallReviewSections so the trailer-review view in App.jsx
// can use the exact same components. Imported at the top of this file.
//
// SummaryCard below is kept here because its content is qualifying-
// call-specific (pain_points, current_state, decision_criteria, …);
// the trailer-review view renders a different summary card (deal brief).

function SummaryCard({ summary }) {
  // Render the JSON summary's structured fields. Skip empties so the
  // panel doesn't waste space on null categories.
  const sections = [
    { label: 'Pain points', items: summary.pain_points },
    { label: 'Current state', items: summary.current_state },
    { label: 'Desired outcomes', items: summary.desired_outcomes },
    { label: 'Decision criteria', items: summary.decision_criteria },
    { label: 'Next steps', items: summary.next_steps },
    { label: 'Stakeholders', items: summary.stakeholders },
  ].filter((s) => Array.isArray(s.items) && s.items.length > 0);

  const singletons = [
    { label: 'Timeline', value: summary.timeline },
    { label: 'Budget signals', value: summary.budget_signals },
  ].filter((s) => s.value);

  if (sections.length === 0 && singletons.length === 0) {
    return null;
  }

  return (
    <div style={{
      background: 'var(--room-card)', borderRadius: 14, padding: 24,
      border: '1px solid var(--room-border)',
    }}>
      <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 700, color: 'var(--room-text-strong)' }}>
        AI summary
      </h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
        {sections.map((s) => (
          <div key={s.label}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--room-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
              {s.label}
            </div>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 14, color: 'var(--room-text-body)', lineHeight: 1.5 }}>
              {s.items.map((item, i) => (<li key={i}>{item}</li>))}
            </ul>
          </div>
        ))}
        {singletons.map((s) => (
          <div key={s.label}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--room-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
              {s.label}
            </div>
            <div style={{ fontSize: 14, color: 'var(--room-text-body)', lineHeight: 1.5 }}>
              {s.value}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Misc ────────────────────────────────────────────────────────

function ErrorBox({ error }) {
  return (
    <div style={{
      padding: '14px 16px', borderRadius: 10,
      background: 'rgba(239, 68, 68, 0.08)', border: '1px solid rgba(239, 68, 68, 0.3)',
      color: '#dc2626', fontSize: 14,
    }}>
      <strong>Couldn't load:</strong> {error}
    </div>
  );
}

function EmptyState({ title, body }) {
  return (
    <div style={{
      padding: '64px 32px', borderRadius: 20, background: 'var(--room-card)',
      border: '1px dashed var(--room-border, #cbd5e1)', textAlign: 'center',
    }}>
      <h3 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 600, color: 'var(--room-text-strong)' }}>{title}</h3>
      <p style={{ margin: 0, color: 'var(--room-text-muted)', fontSize: 14, maxWidth: 520, marginLeft: 'auto', marginRight: 'auto' }}>
        {body}
      </p>
    </div>
  );
}
